
import axios from "axios";
import * as fabric from "fabric";
import api from "./api/axios";
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PdfYearbookImport from "./PdfYearbookImport";
import {
  FiType,
  FiImage,
  FiSquare,
  FiGrid,
  FiPlus,
  FiBold,
  FiItalic,
  FiUnderline,
  FiAlignLeft,
  FiAlignCenter,
  FiAlignRight,
  FiMinus,
  FiPlusCircle,
  FiCopy,
  FiChevronsUp,
  FiChevronsDown,
  FiArrowUp,
  FiArrowDown,
  FiUsers,
  FiBookOpen,
  FiMessageCircle,
} from "react-icons/fi";


import "./designstudio.css";

import PagePreview from "./components/PagePreview";
import StudentList from "./components/StudentList";
import FacultyList from "./components/FacultyList";
import MemoryList from "./components/MemoryList";

import {
  preparePhotos,
  uploadPhoto,
} from "./design/engine/PhotoUploadManager";
import { buildPhotoFrames } from "./design/engine/CanvasPhotoLayout";
import { enableSmartGuides } from "./design/engine/CanvasGuides";

import { applyTemplate } from "./templates";
import { FacultyLayout } from "./templates/layouts/FacultyLayout";

import { buildStudentGroups } from "./buildStudentGroups";

const PAGE_SIZES = {
  portrait: {
    width: 794,
    height: 1123,
  },
};

const STUDENT_LAYOUT_OPTIONS = [
  {
    id: "2x2",
    label: "2 × 2",
    columns: 2,
    capacity: 4,
  },
  {
    id: "3x2",
    label: "3 × 2",
    columns: 3,
    capacity: 6,
  },
  {
    id: "3x3",
    label: "3 × 3",
    columns: 3,
    capacity: 9,
  },
  {
    id: "4x3",
    label: "4 × 3",
    columns: 4,
    capacity: 12,
  },
];

fabric.FabricObject.customProperties = [
  "objectType",
  "studentId",
  "studentName",
  "studentNickname",
  "studentQuote",
  "studentData",
  "studentTextField",
  "facultyId",
  "memoryId",
  "memoryType",
   "cloudUrl",
  "uploadStatus",
];


function DesignStudio() {


  const [showPublishMenu, setShowPublishMenu] = useState(false);
const [showPdfImport, setShowPdfImport] = useState(false);
const [showPublishModal, setShowPublishModal] = useState(false);

const [publishData, setPublishData] = useState({
  title: "",
  batch_name: "",
  academic_year: "",
  description: "",
});

  const navigate = useNavigate();
  const location = useLocation();
  const [yearbookId, setYearbookId] = useState(null);

const canvasRef = useRef(null);
const isLoadingRef = useRef(false);
const canvasReadyRef = useRef(false);
const isBulkUploadRef = useRef(false);
const [scale, setScale] = useState(1);

const selectedObjectRef = useRef(null);
const [isPublishing, setIsPublishing] = useState(false);
const [publishProgress, setPublishProgress] = useState(0);
const [publishStatus, setPublishStatus] = useState("");

const [isSavingDraft, setIsSavingDraft] = useState(false);
const [draftStatus, setDraftStatus] = useState(
  "Preparing your yearbook draft..."
);

// Reusable modal for all Design Studio messages
const [studioModal, setStudioModal] = useState({
  open: false,
  type: "info",
  title: "",
  message: "",
  buttonText: "Okay",
  onConfirm: null,
});

const showStudioModal = ({
  type = "info",
  title = "Notice",
  message = "",
  buttonText = "Okay",
  onConfirm = null,
}) => {
  setStudioModal({
    open: true,
    type,
    title,
    message,
    buttonText,
    onConfirm,
  });
};

const closeStudioModal = () => {
  setStudioModal((previous) => ({
    ...previous,
    open: false,
  }));
};

const handleStudioModalConfirm = () => {
  const action = studioModal.onConfirm;

  closeStudioModal();

  if (typeof action === "function") {
    action();
  }
};

const [imageUploadStatus, setImageUploadStatus] = useState({
  uploading: false,
  total: 0,
  completed: 0,
  failed: 0,
});
/*
=========================================================
STUDENT GROUP TEXT EDITING

When a student name/nickname/quote is being edited,
this stores the actual textbox.

IMPORTANT:
The Fabric ACTIVE object remains the student-group.
=========================================================
*/
const editingStudentTextRef = useRef(null);

const ignoreSelectionClearRef = useRef(false);

const [students, setStudents] = useState([]);
const [showStudentsPanel, setShowStudentsPanel] = useState(false);

// Track students already inserted into the yearbook
const [insertedStudentIds, setInsertedStudentIds] = useState([]);
const [faculty, setFaculty] = useState([]);
const [showFacultyPanel, setShowFacultyPanel] = useState(false);
// Declare memories BEFORE facultyWithMessages uses it.
const [memories, setMemories] = useState([]);
const [showMemoriesPanel, setShowMemoriesPanel] = useState(false);

const facultyWithMessages = faculty.map((member) => {
  const normalizeName = (name) =>
    String(name || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  // Find approved messages belonging to this faculty member.
  const approvedMessages = memories
    .filter((item) => {
      const isFaculty =
        String(item.role || item.type || "").toLowerCase() ===
        "faculty";

      const isApproved =
        String(item.status || "").toLowerCase() === "approved";

      const sameUser =
        member.user_id != null &&
        item.user_id != null &&
        String(member.user_id) === String(item.user_id);

      const sameName =
        normalizeName(member.name || member.full_name) ===
        normalizeName(item.full_name);

      return isFaculty && isApproved && (sameUser || sameName);
    })
    .sort(
      (a, b) =>
        new Date(b.created_at || 0).getTime() -
        new Date(a.created_at || 0).getTime()
    );

  const latestMessage = approvedMessages.find(
    (item) => String(item.message || "").trim()
  );

  return {
    ...member,
    message:
      String(member.message || "").trim() ||
      String(latestMessage?.message || "").trim(),
  };
});


  const [showTemplatePanel, setShowTemplatePanel] = useState(false);

const [activePage, setActivePage] = useState(1);

const activePageRef = useRef(1);

const [cropMode, setCropMode] = useState(false);
const [cropSettings, setCropSettings] = useState(null);
const cropOriginalRef = useRef(null);


const cropPreviewLayoutRef = useRef(null);
const cropDragRef = useRef(null);

const [draggedPageId, setDraggedPageId] = useState(null);
const [dragOverPageId, setDragOverPageId] = useState(null);
const [editorScale, setEditorScale] = useState(1);
const [selectedObject, setSelectedObject] = useState(null);
const [toolbarVisible, setToolbarVisible] = useState(false);
const [toolbarState, setToolbarState] = useState({
  fontWeight: "normal",
  fontStyle: "normal",
  textAlign: "left",
  underline: false,
});

const isTextSelected =
    selectedObject?.type === "textbox" ||
    selectedObject?.objectType ===
        "student-group";

const [pageOrientation, setPageOrientation] =
  useState("portrait");

  const [studentLayout, setStudentLayout] =
  useState("3x3");

const selectedStudentLayout =
  STUDENT_LAYOUT_OPTIONS.find(
    (layout) => layout.id === studentLayout
  ) || STUDENT_LAYOUT_OPTIONS[2];

const getPageSize = (orientation = "portrait") =>
  PAGE_SIZES[orientation] || PAGE_SIZES.portrait;

const cropFrameRef = useRef(null);
const cropKeyboardHandlerRef = useRef(null);

const startCrop = (image) => {
  const canvas = canvasRef.current;

  if (
    !canvas ||
    !image ||
    image.type !== "image" ||
    image.objectType === "student-photo"
  ) {
    return;
  }

  cancelCrop();

  const source = image.getElement?.();
  if (!source) return;

  const scaleX = Math.abs(image.scaleX || 1);
  const scaleY = Math.abs(image.scaleY || 1);

  const displayWidth = image.getScaledWidth();
  const displayHeight = image.getScaledHeight();

  // Initial crop frame covers the displayed image.
  const frame = new fabric.Rect({
    left: image.left,
    top: image.top,
    width: displayWidth,
    height: displayHeight,
    fill: "rgba(0,0,0,0.02)",
    stroke: "#22c55e",
    strokeWidth: 2,
    strokeUniform: true,
    transparentCorners: false,
    cornerColor: "#ffffff",
    cornerStrokeColor: "#22c55e",
    cornerSize: 10,
    borderColor: "#22c55e",
    hasRotatingPoint: false,
    lockRotation: true,
    lockUniScaling: false,
    objectCaching: false,
    selectable: true,
    evented: true,
    excludeFromExport: true,
  });

  cropOriginalRef.current = {
    image,
    source,
    sourceWidth: source.naturalWidth || source.width,
    sourceHeight: source.naturalHeight || source.height,
    originalTransform: image.toObject(
      fabric.FabricObject.customProperties
    ),
    angle: image.angle || 0,
    scaleX,
    scaleY,
  };

  cropFrameRef.current = frame;

  image.set({
    selectable: false,
    evented: false,
  });

  canvas.discardActiveObject();
  canvas.add(frame);
  canvas.setActiveObject(frame);
  canvas.requestRenderAll();

  setCropMode(true);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      applyCrop();
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      cancelCrop();
    }
  };

  cropKeyboardHandlerRef.current = handleKeyDown;
  window.addEventListener("keydown", handleKeyDown, true);
};

const cancelCrop = () => {
  const canvas = canvasRef.current;
  const original = cropOriginalRef.current;
  const frame = cropFrameRef.current;

  if (cropKeyboardHandlerRef.current) {
    window.removeEventListener(
      "keydown",
      cropKeyboardHandlerRef.current,
      true
    );
    cropKeyboardHandlerRef.current = null;
  }

  if (canvas && frame) {
    canvas.remove(frame);
  }

  if (original?.image) {
    original.image.set({
      selectable: original.originalTransform.selectable ?? true,
      evented: original.originalTransform.evented ?? true,
    });
  }

  cropFrameRef.current = null;
  cropOriginalRef.current = null;
  cropDragRef.current = null;

  setCropMode(false);
  setCropSettings(null);

  canvas?.discardActiveObject();
  canvas?.requestRenderAll();
};

const applyCrop = async () => {
  const canvas = canvasRef.current;
  const original = cropOriginalRef.current;
  const frame = cropFrameRef.current;

  if (!canvas || !original || !frame) return;

  const image = original.image;
  const transform = original.originalTransform;

  try {
    // Current implementation supports unrotated images.
    if (Math.abs(original.angle) > 0.01) {
      alert("Please rotate the image back to 0° before cropping.");
      return;
    }

    const imageLeft = Number(transform.left) || 0;
    const imageTop = Number(transform.top) || 0;
    const imageScaleX = Math.abs(Number(transform.scaleX) || 1);
    const imageScaleY = Math.abs(Number(transform.scaleY) || 1);

    const frameLeft = Math.max(imageLeft, frame.left || 0);
    const frameTop = Math.max(imageTop, frame.top || 0);

    const frameRight = Math.min(
      imageLeft + original.sourceWidth * imageScaleX,
      (frame.left || 0) + frame.getScaledWidth()
    );

    const frameBottom = Math.min(
      imageTop + original.sourceHeight * imageScaleY,
      (frame.top || 0) + frame.getScaledHeight()
    );

    const sourceX = Math.max(
      0,
      Math.floor((frameLeft - imageLeft) / imageScaleX)
    );

    const sourceY = Math.max(
      0,
      Math.floor((frameTop - imageTop) / imageScaleY)
    );

    const sourceWidth = Math.min(
      original.sourceWidth - sourceX,
      Math.ceil((frameRight - frameLeft) / imageScaleX)
    );

    const sourceHeight = Math.min(
      original.sourceHeight - sourceY,
      Math.ceil((frameBottom - frameTop) / imageScaleY)
    );

    if (sourceWidth < 1 || sourceHeight < 1) {
      alert("Please select a valid crop area.");
      return;
    }

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = sourceWidth;
    tempCanvas.height = sourceHeight;

    const ctx = tempCanvas.getContext("2d");
    if (!ctx) throw new Error("Unable to create crop canvas.");

    ctx.drawImage(
      original.source,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      sourceWidth,
      sourceHeight
    );

    const croppedImage = await fabric.FabricImage.fromURL(
      tempCanvas.toDataURL("image/png")
    );

    croppedImage.set({
      ...transform,
      left: frameLeft,
      top: frameTop,
      width: sourceWidth,
      height: sourceHeight,
      scaleX: imageScaleX,
      scaleY: imageScaleY,
      cropX: 0,
      cropY: 0,
      selectable: transform.selectable ?? true,
      evented: transform.evented ?? true,
    });

    if (cropKeyboardHandlerRef.current) {
      window.removeEventListener(
        "keydown",
        cropKeyboardHandlerRef.current,
        true
      );
      cropKeyboardHandlerRef.current = null;
    }

    canvas.remove(frame);
    canvas.remove(image);
    canvas.add(croppedImage);
    canvas.setActiveObject(croppedImage);
    canvas.requestRenderAll();

    cropFrameRef.current = null;
    cropOriginalRef.current = null;
    cropDragRef.current = null;

    setCropMode(false);
    setCropSettings(null);

    selectedObjectRef.current = croppedImage;
    setSelectedObject(croppedImage);
    setToolbarVisible(true);

    await saveCurrentPage();
  } catch (error) {
    console.error("Crop failed:", error);
    alert("Unable to crop this image. Please try again.");
  }
};



const isMultiSelection =
  selectedObject?.type === "activeselection";

const showTextToolbar =
  selectedObject?.type === "textbox" ||
  selectedObject?.objectType === "student-group" ||
  isMultiSelection;

const showShapeToolbar =
  !!selectedObject && !showTextToolbar;



const syncSelectedObject = () => {
    const canvas = canvasRef.current;

    const activeObject =
        canvas?.getActiveObject();

    /*
    =====================================================
    IMPORTANT

    If we are editing student text, the GROUP remains
    the selected object.

    The textbox itself is stored separately.
    =====================================================
    */

    if (
        editingStudentTextRef.current &&
        activeObject?.objectType ===
            "student-group"
    ) {
        const textObject =
            editingStudentTextRef.current;

        selectedObjectRef.current =
            activeObject;

        setSelectedObject(activeObject);

        setToolbarVisible(true);

        setToolbarState({
            fontWeight:
                textObject.fontWeight ||
                "normal",

            fontStyle:
                textObject.fontStyle ||
                "normal",

            textAlign:
                textObject.textAlign ||
                "left",

            underline:
                !!textObject.underline,
        });

        return activeObject;
    }

    /*
    =====================================================
    NORMAL OBJECT
    =====================================================
    */

    if (activeObject) {
        selectedObjectRef.current =
            activeObject;

        setSelectedObject(
            activeObject
        );

        setToolbarVisible(true);

        setToolbarState({
            fontWeight:
                activeObject.fontWeight ||
                "normal",

            fontStyle:
                activeObject.fontStyle ||
                "normal",

            textAlign:
                activeObject.textAlign ||
                "left",

            underline:
                !!activeObject.underline,
        });

        return activeObject;
    }

    /*
    =====================================================
    FALLBACK
    =====================================================
    */

    if (selectedObjectRef.current) {
        setSelectedObject(
            selectedObjectRef.current
        );

        setToolbarVisible(true);

        setToolbarState({
            fontWeight:
                selectedObjectRef.current
                    .fontWeight ||
                "normal",

            fontStyle:
                selectedObjectRef.current
                    .fontStyle ||
                "normal",

            textAlign:
                selectedObjectRef.current
                    .textAlign ||
                "left",

            underline:
                !!selectedObjectRef.current
                    .underline,
        });

        return selectedObjectRef.current;
    }

    selectedObjectRef.current =
        null;

    setSelectedObject(null);
    setToolbarVisible(false);

    setToolbarState({
        fontWeight: "normal",
        fontStyle: "normal",
        textAlign: "left",
        underline: false,
    });

    return null;
};

const duplicateObject = async () => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    let obj = canvas.getActiveObject() || selectedObjectRef.current;

    if (!obj) return;

    // Resolve nested student photo/text editing back to the root group.
    const nestedStudentObjects = new Set([
        "student-text",
        "student-photo",
        "student-photo-border",
        "student-photo-placeholder",
    ]);

    if (nestedStudentObjects.has(obj?.objectType)) {
        const group = canvas
            .getObjects()
            .find(
                (item) =>
                    item.objectType === "student-group" &&
                    item._objects?.includes(obj)
            );

        if (group) {
            obj = group;
        }
    }

    if (!obj) return;

    if (obj.objectType !== "student-group") {
        const clone = await obj.clone();

        clone.set({
            left: (obj.left || 0) + 20,
            top: (obj.top || 0) + 20,
        });

        canvas.add(clone);
        canvas.setActiveObject(clone);
        canvas.requestRenderAll();

        selectedObjectRef.current = clone;
        setSelectedObject(clone);
        setToolbarVisible(true);
        editingStudentTextRef.current = null;

        await saveCurrentPage();
        return;
    }

    // Duplicate the entire group as a single Fabric object so future moves use
    // one active object instead of separate child objects.
    const clone = await obj.clone();

    clone.set({
        objectType: "student-group",
        selectable: true,
        evented: true,
        hasControls: true,
        hasBorders: true,
        lockUniScaling: true,
        subTargetCheck: true,
        interactive: true,
        left: (obj.left || 0) + 20,
        top: (obj.top || 0) + 20,
    });

    clone.setControlsVisibility({
        mt: true,
        mb: true,
        ml: true,
        mr: true,
        tl: true,
        tr: true,
        bl: true,
        br: true,
        mtr: true,
    });

    clone.setCoords();

    canvas.add(clone);
    canvas.setActiveObject(clone);
    canvas.requestRenderAll();

    selectedObjectRef.current = clone;
    setSelectedObject(clone);
    setToolbarVisible(true);

    editingStudentTextRef.current = null;

    await saveCurrentPage();
};


const bringToFront = async () => {
  const canvas = canvasRef.current;
  const obj =
    canvas?.getActiveObject() || selectedObjectRef.current;

  if (!obj) return;

  canvas.bringObjectToFront(obj);
  canvas.requestRenderAll();

  await saveCurrentPage();
};

const sendToBack = async () => {
  const canvas = canvasRef.current;
  const obj =
    canvas?.getActiveObject() || selectedObjectRef.current;

  if (!obj) return;

  canvas.sendObjectToBack(obj);
  canvas.requestRenderAll();

  await saveCurrentPage();
};

const bringForward = async () => {
  const canvas = canvasRef.current;
  const obj =
    canvas?.getActiveObject() || selectedObjectRef.current;

  if (!obj) return;

  canvas.bringObjectForward(obj);

  canvas.requestRenderAll();

  await saveCurrentPage();
};

const sendBackward = async () => {
  const canvas = canvasRef.current;
  const obj =
    canvas?.getActiveObject() || selectedObjectRef.current;

  if (!obj) return;

  canvas.sendObjectBackwards(obj);

  canvas.requestRenderAll();

  await saveCurrentPage();
};

const handleApplyTemplate = async (templateName) => {
  const pages = applyTemplate(templateName);

  if (!pages?.length) return;

  setPages(pages);
  pagesRef.current = pages;

  setActivePage(pages[0].id);
  activePageRef.current = pages[0].id;

  await loadPage(pages[0]);
};

const handleUploadImages = async (event) => {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;

  const canvas = canvasRef.current;

  if (!canvas || isBulkUploadRef.current) {
    event.target.value = "";
    return;
  }

  isBulkUploadRef.current = true;

  try {
    const { photos } = preparePhotos(files);

    setImageUploadStatus({
  uploading: true,
  total: photos.length,
  completed: 0,
  failed: 0,
});

    // Load local images before waiting for cloud uploads.
    const loaded = await Promise.all(
      photos.map(async (photo, index) => {
        const img = await fabric.FabricImage.fromURL(photo.src);

        const maxWidth = 300;
        const maxHeight = 300;

        const scale = Math.min(
          maxWidth / (img.width || 1),
          maxHeight / (img.height || 1),
          1
        );

        img.set({
          left: 40 + (index % 8) * 25,
          top: 40 + (index % 8) * 25,
          scaleX: scale,
          scaleY: scale,
          selectable: true,
          evented: true,
          hasControls: true,
          hasBorders: true,
          objectType: "uploaded-photo",
          photoId: photo.id,
          photoName: photo.name,
        });

        img.setControlsVisibility({
          mt: true, mb: true, ml: true, mr: true,
          tl: true, tr: true, bl: true, br: true,
          mtr: true,
        });

        img.setCoords();

        return { photo, img };
      })
    );

    // Show all images immediately.
    canvas.add(...loaded.map(({ img }) => img));

    const lastImage = loaded[loaded.length - 1]?.img;

    if (lastImage) {
      canvas.setActiveObject(lastImage);
      selectedObjectRef.current = lastImage;
      setSelectedObject(lastImage);
      setToolbarVisible(true);
    }

    canvas.requestRenderAll();

    // Upload in the background, three images at a time.

const uploadInBackground = async () => {
  let completed = 0;
  let failed = 0;

  try {
    const concurrency = 3;

    for (let i = 0; i < loaded.length; i += concurrency) {
      const batch = loaded.slice(i, i + concurrency);

      await Promise.all(
        batch.map(async ({ photo, img }) => {
          try {
            const cloudUrl = await uploadPhoto(photo);

            const cloudImage = new Image();
            cloudImage.crossOrigin = "anonymous";

            await new Promise((resolve, reject) => {
              cloudImage.onload = resolve;
              cloudImage.onerror = () =>
                reject(new Error(`Could not load ${photo.name}`));
              cloudImage.src = cloudUrl;
            });

            // Preserve the current displayed dimensions.
            const displayedWidth = img.getScaledWidth();
            const displayedHeight = img.getScaledHeight();

            // Replace the temporary image with the permanent image.
img.setElement(cloudImage);

img.set({
  scaleX: displayedWidth / (img.width || 1),
  scaleY: displayedHeight / (img.height || 1),
  cloudUrl: cloudUrl,
  uploadStatus: "uploaded",
  dirty: true,
});

            // Keep the original local URL before replacing it.
            const localUrl = photo.src;

            photo.src = cloudUrl;
            photo.cloudUrl = cloudUrl;

            if (localUrl?.startsWith("blob:")) {
              URL.revokeObjectURL(localUrl);
            }

            img.setCoords();
            canvas.requestRenderAll();
          } catch (error) {
            failed++;

            img.set({ uploadStatus: "failed" });

            console.error(
              `Cloud upload failed for ${photo.name}:`,
              error
            );
          } finally {
            completed++;

            setImageUploadStatus({
              uploading: true,
              total: loaded.length,
              completed,
              failed,
            });
          }
        })
      );
    }

    if (failed === 0) {
      await saveCurrentPage();
      console.log("All images uploaded and the page was saved.");
    } else {
      alert(
        `${failed} image(s) failed to upload. Remove the failed image(s), upload them again, and wait for success before publishing.`
      );
    }
  } catch (error) {
    console.error("Background image upload error:", error);
    alert("Image uploading encountered an error. Please check your uploads.");
  } finally {
    isBulkUploadRef.current = false;

    setImageUploadStatus((previous) => ({
      ...previous,
      uploading: false,
    }));
  }
};

    // Release the upload lock only after background work finishes.
    void uploadInBackground();

  } catch (error) {
    console.error("Image upload failed:", error);
    isBulkUploadRef.current = false;
  } finally {
    event.target.value = "";
  }
};



const insertMemory = async (memoriesToInsert) => {
  const canvas = canvasRef.current;

  if (!canvas) return;

  const memories = Array.isArray(memoriesToInsert)
    ? memoriesToInsert
    : [memoriesToInsert].filter(Boolean);

  const getImages = (memory) => {
    const value =
      memory.images ??
      memory.image ??
      memory.photos ??
      memory.photo_urls ??
      memory.image_urls;

    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === "string"
            ? item
            : item?.url || item?.secure_url || item?.path || ""
        )
        .filter(Boolean);
    }

    if (typeof value !== "string") return [];

    const raw = value.trim();

    try {
      const parsed = JSON.parse(raw);

      if (Array.isArray(parsed)) {
        return parsed
          .map((item) =>
            typeof item === "string"
              ? item
              : item?.url || item?.secure_url || item?.path || ""
          )
          .filter(Boolean);
      }
    } catch {
      // Continue handling plain image URLs.
    }

    return raw.includes("|||")
      ? raw.split("|||").map((url) => url.trim()).filter(Boolean)
      : raw
        ? [raw]
        : [];
  };

  const resolveUrl = (url) => {
    const src = String(url || "").trim();

    if (!src) return "";

    if (/^(https?:\/\/|data:|blob:)/i.test(src)) {
      return src;
    }

    const baseURL = String(
      api.defaults.baseURL || "https://api.yearbookhub.online"
    ).replace(/\/+$/, "");

    return `${baseURL}/${src.replace(/^\/+/, "")}`;
  };

  try {
    const existingIds = new Set(
      getInsertedMemoryIds().map(String)
    );

    const newMemories = memories.filter((memory) => {
      const id = memory.id ?? memory.memory_id;

      return id == null || !existingIds.has(String(id));
    });

    const pageWidth = canvas.getWidth() || 794;
    const margin = 28;
    const imageAreaWidth = Math.min(300, pageWidth * 0.39);
    const imageAreaHeight = 270;

    for (let index = 0; index < newMemories.length; index++) {
      const memory = newMemories[index];
      const id = memory.id ?? memory.memory_id ?? null;
      const memoryId = id == null ? null : String(id);
      const imageUrls = getImages(memory).map(resolveUrl).filter(Boolean);
      const messageText = String(memory.message || "").trim();

      const blockTop = margin + index * 338;

      if (imageUrls.length) {
        const columns =
          imageUrls.length === 1 ? 1 : imageUrls.length <= 4 ? 2 : 3;

        const rows = Math.ceil(imageUrls.length / columns);
        const cellWidth = imageAreaWidth / columns;
        const cellHeight = imageAreaHeight / rows;

        for (let i = 0; i < imageUrls.length; i++) {
          try {
            const image = await fabric.FabricImage.fromURL(
              imageUrls[i],
              { crossOrigin: "anonymous" }
            );

            const scale = Math.min(
              (cellWidth - 8) / (image.width || 1),
              (cellHeight - 8) / (image.height || 1)
            );

            image.set({
              left: margin + (i % columns) * cellWidth + 4,
              top: blockTop + 8 + Math.floor(i / columns) * cellHeight,
              scaleX: scale,
              scaleY: scale,
              selectable: true,
              evented: true,
              objectType: "memory-photo",
              memoryId,
              memoryType: "student",
            });

            canvas.add(image);
          } catch (error) {
            console.error("Unable to load image:", imageUrls[i], error);
          }
        }
      } else {
        console.warn("No images found for memory:", memory);
      }

      if (messageText) {
        const message = new fabric.Textbox(messageText, {
          left: margin + imageAreaWidth + 20,
          top: blockTop + 14,
          width: Math.max(160, pageWidth - imageAreaWidth - margin * 2 - 20),
          fontSize: 18,
          fontFamily: "Georgia",
          fill: "#222222",
          lineHeight: 1.25,
          textAlign: "left",
          editable: true,
          selectable: true,
          evented: true,
          objectCaching: false,
          objectType: "memory-message",
          memoryId,
          memoryType: "student",
        });

        canvas.add(message);
      }

      console.log("Inserted memory:", {
        id: memoryId,
        imageCount: imageUrls.length,
        hasMessage: Boolean(messageText),
      });
    }

    canvas.requestRenderAll();
    await saveCurrentPage();
  } catch (error) {
    console.error("Failed to insert memories:", error);
  }
};


const insertSelectedMemories = async (selectedMemories) => {
  const canvas = canvasRef.current;

  if (!canvas || !selectedMemories?.length) {
    return;
  }

  try {
    // Insert ALL selected memories at once
    await insertMemory(selectedMemories);

    canvas.renderAll();

    console.log(
      `✅ Added ${selectedMemories.length} memories`
    );

    // Close modal after inserting
    setShowMemoriesPanel(false);

  } catch (error) {
    console.error(
      "❌ Failed to insert selected memories:",
      error
    );
  }
};



const insertFaculty = async (member, index = 0) => {
  const canvas = canvasRef.current;
  if (!canvas || !member) return;

  try {
    console.log("Inserting faculty:", member);

    // Two columns, two rows per A4 portrait page.
    const column = index % 2;
    const row = Math.floor(index / 2);

    const cardX = 25 + column * 380;
    const cardY = 25 + row * 550;
    const cardWidth = 350;

    const facultyId = member.id ?? member.faculty_id ?? null;

    const name =
      member.name ||
      member.full_name ||
      "Faculty Member";

    const position =
      member.position ||
      member.job_title ||
      member.designation ||
      "Faculty";


const normalizeFacultyName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const facultyMessageRecord = (memories || []).find((item) => {
  const isFaculty =
    String(item.role || item.type || "").toLowerCase() === "faculty";

  const isApproved =
    String(item.status || "").toLowerCase() === "approved";

  const sameUser =
    member.user_id &&
    item.user_id &&
    String(member.user_id) === String(item.user_id);

  const sameName =
    normalizeFacultyName(
      member.name || member.full_name
    ) === normalizeFacultyName(item.full_name);

  return isFaculty && isApproved && (sameUser || sameName);
});

const message =
  member.message ||
  facultyMessageRecord?.message ||
  member.quote ||
  member.motto ||
  "";


    // ------------------------------------------
    // 1. FACULTY PHOTO
    // ------------------------------------------
    const photo =
      member.photo ||
      member.profile_pic ||
      member.profilePic ||
      member.photo_url ||
      member.image ||
      "";

    const photoX = cardX + (cardWidth - 180) / 2;
    const photoY = cardY + 15;

    if (photo) {
      try {
        const image = await fabric.FabricImage.fromURL(
          photo.startsWith("http://") ||
          photo.startsWith("https://") ||
          photo.startsWith("data:")
            ? photo
            : `https://api.yearbookhub.online${
                photo.startsWith("/") ? photo : `/${photo}`
              }`,
          { crossOrigin: "anonymous" }
        );

        const maxWidth = 180;
        const maxHeight = 210;

        const imageScale = Math.min(
          maxWidth / (image.width || 1),
          maxHeight / (image.height || 1)
        );

        image.set({
          left:
            photoX +
            (maxWidth - image.width * imageScale) / 2,
          top:
            photoY +
            (maxHeight - image.height * imageScale) / 2,
          scaleX: imageScale,
          scaleY: imageScale,
          selectable: true,
          evented: true,
          objectType: "faculty-photo",
          facultyId,
        });

        canvas.add(image);
      } catch (imageError) {
        console.error("Could not load faculty photo:", imageError);
      }
    }

    // ------------------------------------------
    // 2. FACULTY NAME
    // ------------------------------------------
    canvas.add(
      new fabric.Textbox(String(name), {
        left: cardX,
        top: cardY + 240,
        width: cardWidth,
        fontSize: 23,
        fontWeight: "bold",
        fontFamily: "Poppins",
        fill: "#243B32",
        textAlign: "center",
        editable: true,
        selectable: true,
        evented: true,
        objectCaching: false,
        objectType: "faculty-name",
        facultyId,
      })
    );

    // ------------------------------------------
    // 3. FACULTY POSITION
    // ------------------------------------------
    canvas.add(
      new fabric.Textbox(String(position), {
        left: cardX,
        top: cardY + 278,
        width: cardWidth,
        fontSize: 16,
        fontFamily: "Poppins",
        fill: "#668071",
        textAlign: "center",
        editable: true,
        selectable: true,
        evented: true,
        objectCaching: false,
        objectType: "faculty-position",
        facultyId,
      })
    );

    // ------------------------------------------
    // 4. FACULTY MESSAGE
    // ------------------------------------------
    canvas.add(
      new fabric.Textbox(
        message.trim()
          ? `“${message.trim()}”`
          : "Faculty message not available.",
        {
          left: cardX + 12,
          top: cardY + 315,
          width: cardWidth - 24,
          fontSize: 15,
          fontFamily: "Georgia",
          fontStyle: "italic",
          fill: "#444444",
          textAlign: "center",
          lineHeight: 1.3,
          editable: true,
          selectable: true,
          evented: true,
          objectCaching: false,
          objectType: "faculty-message",
          facultyId,
        }
      )
    );

    canvas.requestRenderAll();

    console.log("Faculty added:", {
      name,
      position,
      photo: Boolean(photo),
      message: Boolean(message),
    });
  } catch (error) {
    console.error("Failed to insert faculty:", error);
  }
};


const insertSelectedFaculty = async (
  selectedFaculty
) => {
  const canvas = canvasRef.current;

  if (
    !canvas ||
    !selectedFaculty?.length
  ) {
    return;
  }

  try {
    for (
      let index = 0;
      index < selectedFaculty.length;
      index++
    ) {
      await insertFaculty(
        selectedFaculty[index],
        index
      );
    }

    canvas.renderAll();

    await saveCurrentPage();

    console.log(
      `✅ Added ${selectedFaculty.length} faculty members`
    );

    // Close faculty modal
    setShowFacultyPanel(false);

  } catch (error) {
    console.error(
      "❌ Failed to insert faculty:",
      error
    );
  }
};

const handleToolbarPointerDown = (event) => {
  event?.stopPropagation?.();
  ignoreSelectionClearRef.current = true;
  syncSelectedObject();
};

const hideToolbar = () => {
  if (selectedObjectRef.current) {
    setToolbarVisible(true);
    return;
  }
  setToolbarVisible(false);
};

const normalizePageJson = (page) => {
  if (!page) return null;

  let pageJson = page.json || page;

  if (typeof pageJson === "string") {
    try {
      pageJson = JSON.parse(pageJson);
    } catch (error) {
      console.error("Failed to parse page JSON:", error);
      return null;
    }
  }

  return pageJson;
};

const hasPageContent = (page) => {
  const pageJson = normalizePageJson(page);
  return Boolean(
    pageJson?.objects?.length ||
      (pageJson?.background && pageJson.background !== "#fff") ||
      pageJson?.backgroundImage
  );
};

const getBestThumbnailPage = (pages) => {
  if (!Array.isArray(pages)) return null;

  const activePage = pages.find((page) =>
    page.id === activePageRef.current && hasPageContent(page)
  );
  if (activePage) return activePage;

  const nonEmptyPage = pages.find(hasPageContent);
  if (nonEmptyPage) return nonEmptyPage;

  return pages[0] || null;
};

const generateThumbnailFromPage = async (pageJson, canvasSize) => {
  const normalized = normalizePageJson({ json: pageJson });
  if (!normalized) return null;

  return new Promise((resolve) => {
    const tempCanvas = new fabric.StaticCanvas(null, {
      width: canvasSize.width,
      height: canvasSize.height,
      backgroundColor: "#fff",
      enableRetinaScaling: true,
    });

    tempCanvas.loadFromJSON(normalized, () => {
      tempCanvas.renderAll();
      resolve(
        tempCanvas.toDataURL({
          format: "png",
          quality: 1,
          multiplier: 2,
        })
      );
    });
  });
};


const getInsertedMemoryIds = () => {
  const canvas = canvasRef.current;

  if (!canvas) return [];

  return [
    ...new Set(
      canvas
        .getObjects()
        .map((obj) => obj.memoryId)
        .filter(
          (id) =>
            id !== undefined &&
            id !== null
        )
        .map(String)
    ),
  ];
};

// Get student IDs from the currently active canvas.
const getInsertedStudentIds = () => {
  const canvas = canvasRef.current;
  if (!canvas) return [];

  return [
    ...new Set(
      canvas
        .getObjects()
        .filter((obj) => obj.objectType === "student-group")
        .map((obj) => obj.studentId)
        .filter(
          (id) =>
            id !== undefined &&
            id !== null &&
            String(id).trim() !== ""
        )
        .map(String)
    ),
  ];
};

// Get student IDs from a saved page's Fabric JSON.
const getStudentIdsFromPageJson = (page) => {
  let pageJson = page?.json;

  if (!pageJson) return [];

  try {
    if (typeof pageJson === "string") {
      pageJson = JSON.parse(pageJson);
    }
  } catch (error) {
    console.warn("Could not parse saved page JSON:", error);
    return [];
  }

  const ids = [];

  const visit = (node) => {
    if (!node || typeof node !== "object") return;

    if (
      node.objectType === "student-group" &&
      node.studentId !== undefined &&
      node.studentId !== null &&
      String(node.studentId).trim() !== ""
    ) {
      ids.push(String(node.studentId));
    }

    if (Array.isArray(node.objects)) {
      node.objects.forEach(visit);
    }
  };

  visit(pageJson);

  return ids;
};

// Get student IDs across the entire yearbook.
const getAllInsertedStudentIds = () => {
  const savedPageIds = (pagesRef.current || []).flatMap(
    getStudentIdsFromPageJson
  );

  const activeCanvasIds = getInsertedStudentIds();

  return [
    ...new Set([...savedPageIds, ...activeCanvasIds].map(String)),
  ];
};

// Keep the student picker synchronized.
const syncInsertedStudentIds = () => {
  const ids = getAllInsertedStudentIds();
  setInsertedStudentIds(ids);
  return ids;
};
const createStudentPage = async (studentChunk) => {
  const canvas = canvasRef.current;

  if (!canvas || !studentChunk?.length) {
    return null;
  }

  try {
    /*
    =====================================================
    1. SAVE CURRENT PAGE
    =====================================================
    */

    await saveCurrentPage();

    /*
    =====================================================
    2. CREATE NEW PAGE
    =====================================================
    */

    const newPage = {
      id: Date.now() + Math.random(),
      title: `Page ${pagesRef.current.length + 1}`,
      type: "students",
      json: null,
      preview: "",
    };

    const updatedPages = [
      ...pagesRef.current,
      newPage,
    ];

    /*
    =====================================================
    3. UPDATE PAGE LIST FIRST
    =====================================================
    */

    const normalizedPages = updatedPages.map(
      (page, index) => ({
        ...page,

        title:
          page.type === "faculty"
            ? page.title
            : index === 0
            ? "Cover"
            : `Page ${index + 1}`,
      })
    );

    pagesRef.current = normalizedPages;
    setPages([...normalizedPages]);

    /*
    =====================================================
    4. SWITCH ACTIVE PAGE
    =====================================================
    */

    activePageRef.current = newPage.id;
    setActivePage(newPage.id);

    /*
    =====================================================
    5. PREVENT AUTOSAVE WHILE CLEARING/SWITCHING
    =====================================================
    */

    isLoadingRef.current = true;

    canvas.clear();

    canvas.backgroundColor = "#ffffff";

    canvas.renderAll();

    /*
    =====================================================
    6. INSERT STUDENTS
    =====================================================
    */

    await buildStudentGroups(
      canvas,
      studentChunk,
      {
        columns: selectedStudentLayout.columns,
        gapX: 15,
        gapY: 15,
        marginX: 20,
        marginY: 20,
        removeExisting: false,
      }
    );

    canvas.requestRenderAll();

    /*
    =====================================================
    7. ENABLE AUTOSAVE AGAIN
    =====================================================
    */

    isLoadingRef.current = false;

    /*
    =====================================================
    8. SAVE NEW PAGE + GENERATE PREVIEW
    =====================================================
    */

    const savedPages = await saveCurrentPage();

    /*
    =====================================================
    9. UPDATE PREVIEW IMMEDIATELY
    =====================================================
    */

    pagesRef.current = savedPages;

    setPages([...savedPages]);

    console.log(
      "📄 Automatically created student page:",
      newPage.id
    );

    return savedPages.find(
      (page) => page.id === newPage.id
    );
  } catch (error) {
    isLoadingRef.current = false;

    console.error(
      "❌ Failed to create student page:",
      error
    );

    return null;
  }
};

const insertStudents = async (selectedStudents) => {
  if (!selectedStudents?.length) {
    return;
  }

  const canvas = canvasRef.current;

  if (!canvas) {
    console.error("❌ Canvas is not ready");
    return;
  }

  try {
    /*
    =====================================================
    CONFIG
    =====================================================
    */

  const STUDENTS_PER_PAGE =
  selectedStudentLayout.capacity;

const STUDENT_COLUMNS =
  selectedStudentLayout.columns;

    /*
    =====================================================
    GET STUDENTS ALREADY IN CURRENT CANVAS
    =====================================================
    */


const existingIds = new Set(
  (pagesRef.current || []).flatMap((page) => {
    let json = page?.json;

    if (!json) return [];

    try {
      if (typeof json === "string") {
        json = JSON.parse(json);
      }
    } catch {
      return [];
    }

    const ids = [];

    const scanObjects = (objects = []) => {
      objects.forEach((obj) => {
        if (
          obj.objectType === "student-group" &&
          obj.studentId != null
        ) {
          ids.push(String(obj.studentId));
        }

        if (Array.isArray(obj.objects)) {
          scanObjects(obj.objects);
        }
      });
    };

    scanObjects(json.objects || []);
    return ids;
  })
);

// Also include students currently visible on the canvas.
getInsertedStudentIds().forEach((id) => {
  existingIds.add(String(id));
});

const studentsToInsert = [];
const selectedIds = new Set();

selectedStudents.forEach((student) => {
  const id =
    student.id ??
    student.student_id ??
    student.studentId;

  if (id == null || String(id).trim() === "") {
    return;
  }

  const key = String(id);

  // Skip students already in the yearbook or duplicated in this selection.
  if (existingIds.has(key) || selectedIds.has(key)) {
    return;
  }

  selectedIds.add(key);
  studentsToInsert.push(student);
});


    if (!studentsToInsert.length) {
      console.warn(
        "⚠️ All selected students are already inserted."
      );

      return;
    }

    /*
    =====================================================
    COUNT STUDENTS CURRENTLY ON PAGE
    =====================================================
    */

    let currentStudentIds =
      getInsertedStudentIds();

    let currentStudentCount =
      currentStudentIds.length;

    console.log(
      "📊 Current page students:",
      currentStudentCount
    );

    console.log(
      "📦 Students to insert:",
      studentsToInsert.length
    );

    /*
    =====================================================
    REMAINING STUDENTS TO PROCESS
    =====================================================
    */

    let remainingStudents = [
      ...studentsToInsert,
    ];

    /*
    =====================================================
    INSERT INTO CURRENT PAGE FIRST
    =====================================================
    */

if (currentStudentCount === 0) {
      const availableSlots =
        STUDENTS_PER_PAGE -
        currentStudentCount;

      const currentPageStudents =
        remainingStudents.slice(
          0,
          availableSlots
        );

      if (currentPageStudents.length) {
        console.log(
          `➕ Adding ${currentPageStudents.length} students to current page`
        );

        await buildStudentGroups(
          canvas,
          currentPageStudents,
          {
            columns: STUDENT_COLUMNS,
            gapX: 15,
            gapY: 15,
            marginX: 20,
            marginY: 20,
            removeExisting: false,
          }
        );

        canvas.requestRenderAll();

        await saveCurrentPage();

        /*
        -------------------------------------------------
        REMOVE INSERTED STUDENTS FROM QUEUE
        -------------------------------------------------
        */

        remainingStudents =
          remainingStudents.slice(
            currentPageStudents.length
          );

        currentStudentCount +=
          currentPageStudents.length;
      }
    }

    /*
    =====================================================
    CREATE AUTOMATIC NEW PAGES
    =====================================================
    */

    while (remainingStudents.length > 0) {
      /*
      ---------------------------------------------------
      TAKE MAXIMUM 9 STUDENTS
      ---------------------------------------------------
      */

      const nextChunk =
        remainingStudents.slice(
          0,
          STUDENTS_PER_PAGE
        );

      console.log(
        "📄 Automatically creating page for:",
        nextChunk.length,
        "students"
      );

      /*
      ---------------------------------------------------
      CREATE PAGE + INSERT STUDENTS
      ---------------------------------------------------
      */

      await createStudentPage(
        nextChunk
      );

      /*
      ---------------------------------------------------
      REMOVE PROCESSED STUDENTS
      ---------------------------------------------------
      */

      remainingStudents =
        remainingStudents.slice(
          nextChunk.length
        );

      /*
      ---------------------------------------------------
      NEW PAGE IS NOW FULL/PARTIALLY FULL
      ---------------------------------------------------
      */

      currentStudentCount =
        nextChunk.length;
    }



    /*
    =====================================================
    FINAL PAGE UPDATE
    =====================================================
    */

    const finalPages =
      pagesRef.current;

    setPages([...finalPages]);

    canvas.requestRenderAll();

    setShowStudentsPanel(false);

    console.log(
      `✅ Added ${studentsToInsert.length} students automatically`
    );

    console.log(
      `📄 Total pages now: ${finalPages.length}`
    );

  } catch (error) {
    console.error(
      "❌ Error inserting students:",
      error
    );
  }
};

const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];

  const bstr = atob(arr[1]);

  let n = bstr.length;

  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File(
    [u8arr],
    filename,
    { type: mime }
  );
};


const handleSaveDraft = async () => {
  if (isSavingDraft || isPublishing) return;

  setIsSavingDraft(true);
  setDraftStatus("Preparing your yearbook draft...");

  try {
    const canvas = canvasRef.current;

    if (!canvas) {
      throw new Error("Canvas is not ready.");
    }

    setDraftStatus("Saving your yearbook pages...");

    canvas.discardActiveObject();
    canvas.renderAll();

    await new Promise((resolve) =>
      requestAnimationFrame(resolve)
    );

    const updatedPages = await saveCurrentPage();

    const pagesToSave = updatedPages.map((page) => ({
      id: page.id,
      title: page.title,
      type: page.type || null,
      facultyPage: page.facultyPage || false,
      orientation:
        page.orientation === "landscape"
          ? "landscape"
          : "portrait",
      json: page.json,
    }));

    setDraftStatus("Generating the cover preview...");

    const coverPage = pagesToSave[0];

    const thumbnail = coverPage?.json
      ? await createThumbnail(
          coverPage.json,
          coverPage.orientation || "portrait"
        )
      : null;

    const payload = {
      title: publishData.title || "Untitled Yearbook",
      batch_name: publishData.batch_name || "",
      academic_year: publishData.academic_year || "",
      description: publishData.description || "",
      pages: pagesToSave,
      thumbnail,
    };

    setDraftStatus("Saving your draft to the server...");

    let response;


const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : "https://api.yearbookhub.online";

if (yearbookId) {
  response = await axios.put(
    `${API_URL}/api/yearbooks/save-draft/${yearbookId}`,
    payload
  );
} else {
  response = await axios.post(
    `${API_URL}/api/yearbooks/save-draft`,
    payload
  );

  if (response.data.yearbookId) {
    setYearbookId(response.data.yearbookId);
  }
}

    // Hide the loading modal and show the success modal.
    setIsSavingDraft(false);

    showStudioModal({
      type: "success",
      title: "Draft Saved Successfully!",
      message:
        "Your yearbook draft has been saved. You can return to the Design Studio and continue editing anytime.",
      buttonText: "Continue Editing",
    });
  } catch (err) {
    console.error(
      "Save draft error:",
      err.response?.data || err
    );

    console.error("Status:", err.response?.status);
    console.error("Message:", err.message);

    setIsSavingDraft(false);

    showStudioModal({
      type: "error",
      title: "Draft Save Failed",
      message:
        err.response?.data?.message ||
        err.message ||
        "Failed to save your yearbook draft. Please try again.",
      buttonText: "Okay",
    });
  }
};


const [pages, setPages] = useState([
  {
    id: 1,
    title: "Cover",
    orientation: "portrait",
    json: null,
  },
]);


const switchPage = async (pageId) => {
  const updatedPages = await saveCurrentPage();

  activePageRef.current = pageId;
  setActivePage(pageId);

  const page = updatedPages.find((p) => p.id === pageId);

  if (page) {
    await loadPage(page);
  }

  // Refresh the inserted-student list after switching.
  const ids = new Set();

  (pagesRef.current || []).forEach((savedPage) => {
    let json = savedPage?.json;

    if (!json) return;

    try {
      if (typeof json === "string") {
        json = JSON.parse(json);
      }
    } catch {
      return;
    }

    const scan = (objects = []) => {
      objects.forEach((obj) => {
        if (
          obj.objectType === "student-group" &&
          obj.studentId != null
        ) {
          ids.add(String(obj.studentId));
        }

        if (Array.isArray(obj.objects)) {
          scan(obj.objects);
        }
      });
    };

    scan(json.objects || []);
  });

  // Include objects on the active canvas.
  getInsertedStudentIds().forEach((id) => {
    ids.add(String(id));
  });

  setInsertedStudentIds([...ids]);
};

const addPage = () => {
  const currentPages = pagesRef.current;

  const currentIndex = currentPages.findIndex(
    (page) => page.id === activePageRef.current
  );

const newPage = {
  id: Date.now(),
  title: `Page ${currentPages.length + 1}`,
  orientation: pageOrientation,
  json: null,
};

  // Insert immediately AFTER the currently active page
  const insertIndex =
    currentIndex === -1
      ? currentPages.length
      : currentIndex + 1;

  const updated = [
    ...currentPages.slice(0, insertIndex),
    newPage,
    ...currentPages.slice(insertIndex),
  ];

  // Re-number page titles
  const normalizedPages = updated.map((page, index) => ({
    ...page,
    title:
      page.type === "faculty"
        ? page.title
        : index === 0
        ? "Cover"
        : `Page ${index + 1}`,
  }));

  pagesRef.current = normalizedPages;
  setPages(normalizedPages);

  activePageRef.current = newPage.id;
  setActivePage(newPage.id);

  requestAnimationFrame(() => {
    loadPage(newPage);
  });
};

const deletePage = async (pageId) => {
  if (!pageId) return;

  /*
   * Don't allow deleting the last page
   */
  if (pagesRef.current.length <= 1) {
    alert("You must have at least one page.");
    return;
  }

  const pageToDelete = pagesRef.current.find(
    (page) => page.id === pageId
  );

  if (!pageToDelete) return;

  const confirmed = window.confirm(
    `Are you sure you want to delete "${pageToDelete.title}"?`
  );

  if (!confirmed) return;

  /*
   * Save the currently active page first
   */
  let currentPages = pagesRef.current;

  if (
    activePageRef.current &&
    canvasRef.current
  ) {
    currentPages = await saveCurrentPage();
  }

  /*
   * Find the page index BEFORE deleting
   */
  const deletedIndex = currentPages.findIndex(
    (page) => page.id === pageId
  );

  /*
   * Delete page
   */
  let updatedPages = currentPages.filter(
    (page) => page.id !== pageId
  );

  /*
   * Re-number normal pages
   */
  updatedPages = updatedPages.map(
    (page, index) => ({
      ...page,

      title:
        page.type === "faculty"
          ? page.title
          : index === 0
          ? "Cover"
          : `Page ${index + 1}`,
    })
  );

  /*
   * Update React + ref
   */
  pagesRef.current = updatedPages;
  setPages(updatedPages);

  /*
   * If deleted page was active,
   * open another page.
   */
  if (activePageRef.current === pageId) {
    const newIndex = Math.min(
      deletedIndex,
      updatedPages.length - 1
    );

    const nextPage =
      updatedPages[newIndex];

    activePageRef.current = nextPage.id;
    setActivePage(nextPage.id);

    requestAnimationFrame(() => {
      loadPage(nextPage);
    });
  }

  console.log(
    `🗑️ Deleted page: ${pageToDelete.title}`
  );
};

const addFacultyPage = () => {

    const facultyPages = pagesRef.current.filter(
        page => page.type === "faculty"
    );


    const newFacultyPage = {
        id: Date.now(),

        title:
        `Faculty ${facultyPages.length + 1}`,

        type:"faculty",

        facultyPage:true,

        json: FacultyLayout(),
    };


    const updated = [
        ...pagesRef.current.slice(
            0,
            pagesRef.current.length
        ),
        newFacultyPage
    ];


    pagesRef.current = updated;

    setPages(updated);


    activePageRef.current =
        newFacultyPage.id;

    setActivePage(newFacultyPage.id);


    requestAnimationFrame(()=>{
        loadPage(newFacultyPage);
    });

};

const calculateInitialScale = useCallback(() => {
  const workspace = document.querySelector(".workspace");

  if (!workspace) return;

  const availableWidth = workspace.clientWidth - 40;
  const availableHeight = workspace.clientHeight - 40;

const size =
  PAGE_SIZES[pageOrientation] || PAGE_SIZES.portrait;

const widthScale = availableWidth / size.width;
const heightScale = availableHeight / size.height;

  const fitScale = Math.min(widthScale, heightScale);

  setScale(Math.min(fitScale, 1));
}, [pageOrientation]);

useEffect(() => {
  const timer = setTimeout(() => {
    calculateInitialScale();
  }, 50);

  return () => clearTimeout(timer);
}, [calculateInitialScale]);

const loadPage = async (page) => {
  const canvas = canvasRef.current;
  if (!canvas) return;


isLoadingRef.current = true;

try {
  const orientation = page?.orientation || "portrait";
  const size = PAGE_SIZES[orientation] || PAGE_SIZES.portrait;

  setPageOrientation(orientation);

  canvas.setDimensions({
    width: size.width,
    height: size.height,
  });

  canvas.clear();


    // Always reset the Fabric canvas background first
    canvas.backgroundColor = "#ffffff";

    if (!page?.json) {
      canvas.renderAll();
      return;
    }

    console.log("Loading page:", page);

    let pageJson = page.json;

    // Handle JSON saved as string
    if (typeof pageJson === "string") {
      pageJson = JSON.parse(pageJson);
    }

   await canvas.loadFromJSON(pageJson);

// Restore student-group movement after loading a page.
canvas.getObjects().forEach((obj) => {
  if (obj.objectType !== "student-group") return;

  obj.set({
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    lockMovementX: false,
    lockMovementY: false,
    lockScalingX: false,
    lockScalingY: false,
    lockRotation: false,
    subTargetCheck: true,
    interactive: true,
  });

  // Keep the photo and border attached to the group.
  obj.getObjects().forEach((child) => {
    if (
      child.objectType === "student-photo" ||
      child.objectType === "student-photo-border" ||
      child.objectType === "student-photo-placeholder"
    ) {
      child.set({
        selectable: false,
        evented: false,
      });
    }

    // Text stays available for your existing text-editing handler,
    // but cannot be moved separately from the student group.

if (child.objectType === "student-text") {
  child.set({
    selectable: true,
    evented: true,
  });
}

  });

  obj.setControlsVisibility({
    mt: true,
    mb: true,
    ml: true,
    mr: true,
    tl: true,
    tr: true,
    bl: true,
    br: true,
    mtr: true,
  });

  obj.setCoords();
});

canvas.requestRenderAll();

    // IMPORTANT:
    // Force white page background after template JSON loads
    canvas.backgroundColor = "#ffffff";

    canvas.renderAll();

  } catch (error) {
    console.error("❌ Failed to load page:", error);

    canvas.clear();
    canvas.backgroundColor = "#ffffff";
    canvas.renderAll();

  } finally {
    isLoadingRef.current = false;
  }
};


const handlePdfPagesImported = async (pdfPages) => {
  const canvas = canvasRef.current;

  if (!canvas || !pdfPages?.length) {
    throw new Error("The Design Studio canvas is not ready.");
  }

  // Protect existing work before replacing the current pages.
  const hasExistingContent =
    canvas.getObjects().length > 0 ||
    pagesRef.current.some((page) => {
      const json = normalizePageJson(page);
      return Boolean(json?.objects?.length);
    });

  if (
    hasExistingContent &&
    !window.confirm(
      "Importing this PDF will replace the current Design Studio pages. Continue?"
    )
  ) {
    return false;
  }

  const importedPages = [];
  const temporaryCanvas = new fabric.StaticCanvas(null, {
    width: 794,
    height: 1123,
    backgroundColor: "#ffffff",
  });

  try {
    for (let index = 0; index < pdfPages.length; index++) {
      const pdfPage = pdfPages[index];

      if (!pdfPage?.imageUrl) {
        throw new Error(`PDF page ${index + 1} has no image URL.`);
      }

      temporaryCanvas.clear();
      temporaryCanvas.setDimensions({
        width: 794,
        height: 1123,
      });
      temporaryCanvas.backgroundColor = "#ffffff";

      const image = await fabric.FabricImage.fromURL(
        pdfPage.imageUrl,
        { crossOrigin: "anonymous" }
      );

      const imageScaleX = 794 / (image.width || 794);
      const imageScaleY = 1123 / (image.height || 1123);

      image.set({
        left: 0,
        top: 0,
        originX: "left",
        originY: "top",
        scaleX: imageScaleX,
        scaleY: imageScaleY,

        // Keep the finished PDF page in place on the canvas.
        selectable: false,
        evented: false,
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,

        objectType: "pdf-page-image",
      });

      temporaryCanvas.add(image);
      temporaryCanvas.renderAll();

      importedPages.push({
        id: Date.now() + index,
        title: index === 0 ? "Cover" : `Page ${index + 1}`,
        type: "pdf-image",
        orientation: "portrait",
        imageUrl: pdfPage.imageUrl,
        preview: pdfPage.imageUrl,
        json: temporaryCanvas.toJSON(),
      });
    }
  } finally {
    temporaryCanvas.dispose();
  }

  if (!importedPages.length) {
    throw new Error("No PDF pages were imported.");
  }

  // Replace the page list and synchronize the reference immediately.
  pagesRef.current = importedPages;
  setPages(importedPages);

  activePageRef.current = importedPages[0].id;
  setActivePage(importedPages[0].id);

  // Display the first imported page in the real Design Studio canvas.
  await loadPage(importedPages[0]);

  setPageOrientation("portrait");
  setShowPdfImport(false);

  showStudioModal({
    type: "success",
    title: "PDF Imported Successfully",
    message:
      `${importedPages.length} PDF page(s) are now in Design Studio. ` +
      "Review your pages, then click Publish to enter your yearbook details.",
    buttonText: "Continue Editing",
  });

  return true;
};


const pagesRef = useRef(pages);

useEffect(() => {
  pagesRef.current = pages;
}, [pages]);

  const [showShapesPanel, setShowShapesPanel] = useState(false);

const savePage = async (pageId) => {
  const canvas = canvasRef.current;
  if (!canvas) return pagesRef.current;

  const json = canvas.toJSON();

const preview = await createThumbnail(json);

  const updated = pagesRef.current.map((p) =>
    p.id === pageId
      ? {
          ...p,
          json,
          preview,
        }
      : p
  );

  pagesRef.current = updated;
  setPages(updated);

  return updated;
};

useEffect(() => {
    const workspace = document.querySelector(".workspace");

    if (!workspace) return;

    const updateEditorScale = () => {
        const rect = workspace.getBoundingClientRect();

        const padding = 24;

        const availableWidth = rect.width - padding;
        const availableHeight = rect.height - padding;

  const size =
  PAGE_SIZES[pageOrientation] || PAGE_SIZES.portrait;

const widthScale = availableWidth / size.width;
const heightScale = availableHeight / size.height;

        const scale = Math.min(
            widthScale,
            heightScale,
            1
        );

        setEditorScale(Math.max(scale, 0.25));
    };

    const observer = new ResizeObserver(updateEditorScale);

    observer.observe(workspace);

    updateEditorScale();

    return () => observer.disconnect();
}, [pageOrientation]);

  useEffect(() => {
  activePageRef.current = activePage;
}, [activePage]);

  // KEYBOARD DELETE SUPPORT
useEffect(() => {
  const handleKeyDown = async (e) => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    /*
    =====================================================
    DON'T DELETE WHILE TYPING
    =====================================================
    */

    const activeElement =
      document.activeElement;

    const isTyping =
      activeElement?.tagName === "INPUT" ||
      activeElement?.tagName === "TEXTAREA" ||
      activeElement?.isContentEditable;

    /*
    Student textbox editing is handled specially.
    */

    const editingText =
      editingStudentTextRef.current;

    if (editingText?.isEditing) {
      return;
    }

    /*
    =====================================================
    DELETE / BACKSPACE
    =====================================================
    */


    // MOVE SELECTED OBJECT WITH ARROW KEYS
    const arrowKeys = [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
    ];

    if (arrowKeys.includes(e.key)) {
      if (isTyping || editingText?.isEditing) return;

      let activeObject =
        canvas.getActiveObject() ||
        selectedObjectRef.current;

      if (!activeObject) return;

      // Move the complete student group, not just its child.
      activeObject = getRootStudentGroup(
        canvas,
        activeObject
      );

      if (!activeObject) return;

      e.preventDefault();

      // Normal movement: 1 canvas unit
      // Shift + arrow: 10 canvas units
      const step = e.shiftKey ? 10 : 1;

      let left = activeObject.left || 0;
      let top = activeObject.top || 0;

      if (e.key === "ArrowLeft") left -= step;
      if (e.key === "ArrowRight") left += step;
      if (e.key === "ArrowUp") top -= step;
      if (e.key === "ArrowDown") top += step;

      activeObject.set({ left, top });
      activeObject.setCoords();

      canvas.requestRenderAll();
      return;
    }

    // KEEP EXISTING DELETE / BACKSPACE SUPPORT
    if (
      e.key !== "Delete" &&
      e.key !== "Backspace"
    ) {
      return;
    }


    if (isTyping) {
      return;
    }

    let activeObject =
      canvas.getActiveObject() ||
      selectedObjectRef.current;

    if (!activeObject) return;

    /*
    =====================================================
    STUDENT CHILD → STUDENT GROUP
    =====================================================
    */

    activeObject =
      getRootStudentGroup(
        canvas,
        activeObject
      );

    e.preventDefault();

    /*
    =====================================================
    DELETE
    =====================================================
    */
const deletedStudentId =
  activeObject?.objectType === "student-group"
    ? activeObject.studentId
    : null;

canvas.discardActiveObject();

canvas.remove(activeObject);

editingStudentTextRef.current = null;
selectedObjectRef.current = null;

setSelectedObject(null);
setToolbarVisible(false);

/*
=====================================================
SYNC STUDENT LIST
=====================================================
*/

if (deletedStudentId !== null) {
  setInsertedStudentIds(
    getInsertedStudentIds()
  );
}

canvas.requestRenderAll();

await saveCurrentPage();    canvas.discardActiveObject();

    canvas.remove(activeObject);

    editingStudentTextRef.current = null;
    selectedObjectRef.current = null;

    setSelectedObject(null);
    setToolbarVisible(false);

    canvas.requestRenderAll();

    await saveCurrentPage();

    console.log(
      "🗑️ Deleted:",
      activeObject.objectType
    );
  };

  
  const handleKeyUp = async (e) => {
    if (
      ![
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(e.key)
    ) {
      return;
    }

    const activeElement = document.activeElement;

    if (
      activeElement?.tagName === "INPUT" ||
      activeElement?.tagName === "TEXTAREA" ||
      activeElement?.isContentEditable ||
      editingStudentTextRef.current?.isEditing
    ) {
      return;
    }

    await saveCurrentPage();
  };


window.addEventListener("keydown", handleKeyDown);
window.addEventListener("keyup", handleKeyUp);

return () => {
  window.removeEventListener("keydown", handleKeyDown);
  window.removeEventListener("keyup", handleKeyUp);
};
}, []);



useEffect(() => {
  let cancelled = false;

  const fetchMemories = async () => {
    try {
      const res = await api.get("/api/admin/memories");

      // Support either a direct array or an object response.
      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.memories)
          ? res.data.memories
          : [];

      const approved = data.filter(
        (item) =>
          String(item.status || "").toLowerCase() === "approved"
      );

      if (!cancelled) {
        setMemories(approved);
      }
    } catch (err) {
      console.error(
        "Failed to fetch memories:",
        err.response?.status,
        err.response?.data || err.message
      );
    }
  };

  fetchMemories();

  return () => {
    cancelled = true;
  };
}, []);


  useEffect(() => {
    axios
      .get("https://api.yearbookhub.online/api/students/yearbook", {
        withCredentials: true,
      })
      .then((res) => {
        const data = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.students)
            ? res.data.students
            : [];

        setStudents(data);
      })
      .catch((err) => {
        console.error("Failed to fetch students for design studio:", err);
        setStudents([]);
      });
  }, []);


useEffect(() => {
  api
    .get("/api/faculty/yearbook")
    .then((res) => {
      const data = Array.isArray(res?.data) ? res.data : [];
      console.log("FACULTY API RESPONSE:", data);
      setFaculty(data);
    })
    .catch((err) => {
      console.error(
        "Failed to fetch faculty:",
        err.response?.data || err.message
      );
      setFaculty([]);
    });
}, []);


useEffect(() => {
  if (!location.state) return;

  const { pages: rawPages = [], yearbookId } = location.state;

  let parsedPages = [];

  try {
    parsedPages =
      typeof rawPages === "string"
        ? JSON.parse(rawPages)
        : rawPages;
  } catch (e) {
    parsedPages = [];
  }

  if (parsedPages.length) {
    setPages(parsedPages);
    pagesRef.current = parsedPages;

    setActivePage(parsedPages[0].id);
    activePageRef.current = parsedPages[0].id;

    requestAnimationFrame(() => {
      loadPage(parsedPages[0]);
    });
  }

  if (yearbookId) {
    setYearbookId(yearbookId);
  }
}, [location.state]);

  // ADD TEXT
  const addText = () => {

const text = new fabric.Textbox("New Text", {
  left: 100,
  top: 100,
  width: 200,
  fontSize: 28,
  fontFamily: "Arial",
  fill: "#000000",
});

    canvasRef.current.add(text);

    canvasRef.current.setActiveObject(text);

    canvasRef.current.renderAll();
  };

  // ADD RECTANGLE
  const addShape = () => {

    const rect = new fabric.Rect({
      left: 150,
      top: 150,
      fill: "green",
      width: 120,
      height: 120,
    });

    canvasRef.current.add(rect);

    canvasRef.current.setActiveObject(rect);

    canvasRef.current.renderAll();
  };

  // ADD CIRCLE
  const addCircle = () => {

    const circle =
      new fabric.Circle({
        radius: 50,
        fill: "blue",
        left: 200,
        top: 200,
      });

    canvasRef.current.add(circle);

    canvasRef.current.setActiveObject(circle);

    canvasRef.current.renderAll();
  };

  // ADD TRIANGLE
  const addTriangle = () => {

    const triangle =
      new fabric.Triangle({
        width: 100,
        height: 100,
        fill: "orange",
        left: 250,
        top: 250,
      });

    canvasRef.current.add(triangle);

    canvasRef.current.setActiveObject(
      triangle
    );

    canvasRef.current.renderAll();
  };


    // SAVE CURRENT PAGE

const saveCurrentPage = async () => {
  const canvas = canvasRef.current;

  if (!canvas) {
    return pagesRef.current;
  }

  // Serialize the current canvas.
  const json = canvas.toJSON();

  // Ensure uploaded images use permanent Cloudinary URLs.
  const fixImageSources = (node) => {
    if (!node || typeof node !== "object") return;

    if (
      node.type === "Image" &&
      typeof node.cloudUrl === "string" &&
      node.cloudUrl.startsWith("https://")
    ) {
      node.src = node.cloudUrl;
    }

    if (Array.isArray(node.objects)) {
      node.objects.forEach(fixImageSources);
    }

    if (node.backgroundImage) {
      fixImageSources(node.backgroundImage);
    }
  };

  fixImageSources(json);

  // Generate the preview using the corrected JSON.
  const currentOrientation =
  pagesRef.current.find(
    (page) => page.id === activePageRef.current
  )?.orientation || pageOrientation;

const preview = await createThumbnail(json, currentOrientation);

const updated = pagesRef.current.map((page) =>
  page.id === activePageRef.current
    ? {
        ...page,
        json,
        orientation: pageOrientation,
        preview: preview || page.preview || page.thumbnail || "",
      }
    : page
);

  // Update both React state and the synchronous reference.
  pagesRef.current = updated;
  setPages(updated);

  return updated;
};


const handleOrientationChange = async (orientation) => {
  if (!PAGE_SIZES[orientation]) return;
  if (orientation === pageOrientation) return;

  const canvas = canvasRef.current;
  if (!canvas) return;

  // Save the current page before changing its dimensions.
  const savedPages = await saveCurrentPage();

  const activeId = activePageRef.current;
  const size = PAGE_SIZES[orientation];

  // Update the Fabric canvas to the selected A4 orientation.
  canvas.setDimensions({
    width: size.width,
    height: size.height,
  });

  canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
  canvas.backgroundColor = "#ffffff";

  // Save the orientation with this specific page.
  const updatedPages = savedPages.map((page) =>
    page.id === activeId
      ? { ...page, orientation }
      : page
  );

  pagesRef.current = updatedPages;
  setPages(updatedPages);
  setPageOrientation(orientation);

  canvas.requestRenderAll();
};



const saveAsTemplate = async () => {
  const canvas = canvasRef.current;

  await saveCurrentPage();

  const allPages = pagesRef.current;

  // 🔥 generate thumbnail from FIRST page
  let thumbnail = null;

  if (allPages?.[0]?.json) {
    thumbnail = await generateThumbnailFromPage(
      allPages[0].json,
      {
        width: canvas.width,
        height: canvas.height,
      }
    );
  }

  try {
    await axios.post("https://api.yearbookhub.online/api/themes/save-template", {
      theme_name: "Custom Template",
      description: "Created from design studio",
      created_by: 3,
      pages: allPages,
      thumbnail, // 🔥 ADD THIS
    });

    alert("Template saved with preview!");
  } catch (err) {
    console.log(err);
  }
};

const customizeTheme = async (theme) => {
  const res = await axios.post(
    "https://api.yearbookhub.online/api/yearbooks/create-from-theme",
    {
      title: "My Yearbook 2026",
      theme_id: theme.id,
    }
  );

  navigate("/admin/design", {
    state: {
      pages: res.data.theme_pages,
      yearbookId: res.data.yearbook_id,
    },
  });
};

const saveYearbook = async () => {
  await axios.put(
    `https://api.yearbookhub.online/api/yearbooks/${yearbookId}`,
    {
      pages: pagesRef.current,
    }
  );

  alert("Yearbook Saved");
};

const getRootStudentGroup = (canvas, object) => {
  if (!canvas || !object) return object;

  if (object.objectType === "student-group") {
    return object;
  }

  const studentObjects = new Set([
    "student-text",
    "student-photo",
    "student-photo-border",
    "student-photo-placeholder",
  ]);

  if (studentObjects.has(object.objectType)) {
    const group = canvas
      .getObjects()
      .find(
        (item) =>
          item.objectType === "student-group" &&
          item._objects?.includes(object)
      );

    if (group) {
      return group;
    }
  }

  return object;
};

  // DELETE OBJECT
const deleteObject = async () => {
  const canvas = canvasRef.current;

  if (!canvas) return;

  let activeObject =
    canvas.getActiveObject() ||
    selectedObjectRef.current;

  if (!activeObject) return;

  /*
  =====================================================
  IF STUDENT TEXT IS BEING EDITED
  =====================================================
  */

  const editingText =
    editingStudentTextRef.current;

  if (editingText) {
    const group = canvas
      .getObjects()
      .find(
        (obj) =>
          obj.objectType === "student-group" &&
          obj._objects?.includes(editingText)
      );

    if (group) {
      activeObject = group;
    }
  }

  /*
  =====================================================
  RESOLVE STUDENT CHILD → STUDENT GROUP
  =====================================================
  */

  activeObject = getRootStudentGroup(
    canvas,
    activeObject
  );

  /*
  =====================================================
  SAVE ID BEFORE REMOVING
  =====================================================
  */

  const deletedStudentId =
    activeObject?.objectType === "student-group"
      ? activeObject.studentId
      : null;

  /*
  =====================================================
  DELETE
  =====================================================
  */

  canvas.discardActiveObject();

  canvas.remove(activeObject);

  editingStudentTextRef.current = null;
  selectedObjectRef.current = null;

  setSelectedObject(null);
  setToolbarVisible(false);

  /*
  =====================================================
  UPDATE STUDENT TRACKING
  =====================================================
  */

  if (deletedStudentId !== null) {
    setInsertedStudentIds(
      getInsertedStudentIds()
    );
  }

  canvas.requestRenderAll();

  await saveCurrentPage();

  console.log(
    "🗑️ Deleted object:",
    activeObject.objectType,
    deletedStudentId
  );
};
  // CHANGE COLOR
const changeColor = async (color) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const activeObject =
    canvas.getActiveObject() || selectedObjectRef.current;

  if (!activeObject) return;

  // If editing a student's text directly, change that text only.
  const editingText = editingStudentTextRef.current;

  if (editingText?.objectType === "student-text") {
    editingText.set("fill", color);
    editingText.dirty = true;
  } else {
    // Support Shift-selected objects.
    const targets =
      activeObject.type === "activeselection"
        ? activeObject.getObjects()
        : [activeObject];

    targets.forEach((obj) => {
      if (
        obj.type === "textbox" ||
        obj.type === "text" ||
        obj.type === "i-text" ||
        obj.objectType === "student-text" ||
        obj.objectType === "student-group" ||
        obj.type === "rect" ||
        obj.type === "circle" ||
        obj.type === "triangle"
      ) {
        obj.set("fill", color);
        obj.dirty = true;
        obj.setCoords();
      }
    });
  }

  canvas.requestRenderAll();
  await saveCurrentPage();
};


const getSelectedTextObjects = () => {
  const canvas = canvasRef.current;
  if (!canvas) return [];

  const activeObject =
    canvas.getActiveObject() || selectedObjectRef.current;

  // When editing text inside a student card, use that text.
  if (editingStudentTextRef.current) {
    return [editingStudentTextRef.current];
  }

  // Shift-selected objects.
  if (activeObject?.type === "activeselection") {
    return activeObject.getObjects().filter((obj) =>
      ["textbox", "text", "i-text"].includes(obj.type) ||
      obj.objectType === "student-text"
    );
  }

  // A regular textbox.
  if (
    ["textbox", "text", "i-text"].includes(activeObject?.type) ||
    activeObject?.objectType === "student-text"
  ) {
    return [activeObject];
  }

  // Text inside a selected student group.
  if (activeObject?.objectType === "student-group") {
    return (activeObject.getObjects?.() || []).filter(
      (obj) => obj.objectType === "student-text"
    );
  }

  return [];
};

const refreshTextObjects = async (textObjects) => {
  const canvas = canvasRef.current;
  if (!canvas || !textObjects.length) return;

  textObjects.forEach((obj) => {
    obj.initDimensions?.();
    obj.setCoords?.();
  });

  const activeObject = canvas.getActiveObject();
  activeObject?.setCoords?.();

  canvas.requestRenderAll();
  await saveCurrentPage();
};


  // CHANGE FONT SIZE

const changeFontSize = async (size) => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const activeObject =
    canvas.getActiveObject() ||
    selectedObjectRef.current;

  const newSize = Math.max(6, Number(size) || 6);

  // Keep normal student-text editing working.
  const editingText = editingStudentTextRef.current;

  if (editingText) {
    editingText.set({ fontSize: newSize });
    editingText.initDimensions();
    editingText.setCoords();

    const parentGroup = canvas.getObjects().find(
      (obj) =>
        obj.objectType === "student-group" &&
        obj.getObjects?.().includes(editingText)
    );

    parentGroup?.setCoords();
  } else if (activeObject?.type === "activeselection") {
    // Apply the font size to every selected text object.
    activeObject.getObjects().forEach((obj) => {
      if (
        obj.type === "textbox" ||
        obj.type === "text" ||
        obj.type === "i-text" ||
        obj.objectType === "student-text"
      ) {
        obj.set({ fontSize: newSize });
        obj.initDimensions?.();
        obj.setCoords();
      }
    });

    activeObject.setCoords();
  } else if (
    activeObject?.type === "textbox" ||
    activeObject?.type === "text" ||
    activeObject?.type === "i-text"
  ) {
    activeObject.set({ fontSize: newSize });
    activeObject.initDimensions?.();
    activeObject.setCoords();
  } else {
    return;
  }

  canvas.requestRenderAll();
  await saveCurrentPage();
};


const publishYearbook = async () => {
  if (isPublishing) return;

  const canvas = canvasRef.current;

  const unresolvedImages = canvas
    ? canvas.getObjects().filter((obj) => {
        if (obj.objectType !== "uploaded-photo") return false;

        const src = obj.getSrc?.() || obj._element?.src || "";

        return (
          obj.uploadStatus === "uploading" ||
          obj.uploadStatus === "failed" ||
          src.startsWith("blob:")
        );
      })
    : [];

if (imageUploadStatus.uploading || unresolvedImages.length > 0) {
  showStudioModal({
    type: "warning",
    title: "Images Still Uploading",
    message:
      "Please wait until all images upload successfully before publishing your yearbook.",
    buttonText: "Got it",
  });

  return;
}

  setIsPublishing(true);
  setPublishProgress(0);
  setPublishStatus("Preparing your yearbook...");

  try {
    const canvas = canvasRef.current;

    if (!canvas) {
      throw new Error("Canvas is not ready.");
    }

    setPublishStatus("Saving pages and preparing cover...");
    const updatedPages = await saveCurrentPage();

    console.table(
  updatedPages.map((page) => ({
    id: page.id,
    title: page.title,
    orientation: page.orientation,
  }))
);

const pagesForPublish = updatedPages.map((page) => {
  const json =
    typeof page.json === "string"
      ? JSON.parse(page.json)
      : page.json;

  const orientation =
    page.orientation === "portrait" ||
    page.orientation === "landscape"
      ? page.orientation
      : Number(json?.width) > Number(json?.height)
        ? "landscape"
        : "portrait";

  return {
    id: page.id,
    title: page.title,
    orientation,
    type: page.type || null,
    facultyPage: page.facultyPage || false,
    json: page.json,
  };
});

    const coverPage = pagesForPublish[0];

const thumbnail = coverPage?.json
  ? await createThumbnail(
      coverPage.json,
      coverPage.orientation || "portrait"
    )
  : null;

    const payload = {
      title: publishData.title,
      batch_name: publishData.batch_name,
      academic_year: publishData.academic_year,
      description: publishData.description,
      pages: pagesForPublish,
      thumbnail,
    };

    setPublishStatus("Uploading yearbook...");

    const config = {
      onUploadProgress: (event) => {
        if (event.total) {
          const percent = Math.round(
            (event.loaded * 100) / event.total
          );

          setPublishProgress(percent);
          setPublishStatus(
            percent >= 100
              ? "Upload complete. Finalizing publication..."
              : `Uploading yearbook... ${percent}%`
          );
        }
      },
    };


const API_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:5000"
    : "https://api.yearbookhub.online";

let response;

if (yearbookId) {
  response = await axios.put(
    `${API_URL}/api/yearbooks/publish/${yearbookId}`,
    payload,
    config
  );
} else {
  response = await axios.post(
    `${API_URL}/api/yearbooks/publish`,
    payload,
    config
  );

  if (response.data.yearbookId) {
    setYearbookId(response.data.yearbookId);
  }
}


setPublishProgress(100);
setPublishStatus("Publication successful!");

setShowPublishModal(false);
setShowPublishMenu(false);

showStudioModal({
  type: "success",
  title: "Yearbook Published!",
  message:
    "Your yearbook has been published successfully and is ready to view.",
  buttonText: "Go to Yearbooks",
  onConfirm: () => navigate("/admin/yearbooks"),
});
  } catch (err) {
    console.error(
      "Publish yearbook error:",
      err.response?.data || err
    );

showStudioModal({
  type: "error",
  title: "Publication Failed",
  message:
    err.response?.data?.message ||
    err.message ||
    "Failed to publish yearbook. Please try again.",
  buttonText: "Close",
});
  } finally {
    setIsPublishing(false);
    setPublishProgress(0);
    setPublishStatus("");
  }
};

// CHANGE FONT FAMILY
const changeFontFamily = async (fontFamily) => {
  const textObjects = getSelectedTextObjects();
  if (!textObjects.length) return;

  textObjects.forEach((obj) => {
    obj.set({ fontFamily });
  });

  setToolbarVisible(true);
  await refreshTextObjects(textObjects);
};

const increaseFont = async () => {
  const canvas = canvasRef.current;
  const activeObject = canvas?.getActiveObject();

  const currentSize =
    editingStudentTextRef.current?.fontSize ||
    (activeObject?.type === "activeselection"
      ? activeObject.getObjects().find(
          (obj) =>
            ["textbox", "text", "i-text"].includes(obj.type) ||
            obj.objectType === "student-text"
        )?.fontSize
      : activeObject?.fontSize) ||
    20;

  await changeFontSize(currentSize + 2);
};

const decreaseFont = async () => {
  const canvas = canvasRef.current;
  const activeObject = canvas?.getActiveObject();

  const currentSize =
    editingStudentTextRef.current?.fontSize ||
    (activeObject?.type === "activeselection"
      ? activeObject.getObjects().find(
          (obj) =>
            ["textbox", "text", "i-text"].includes(obj.type) ||
            obj.objectType === "student-text"
        )?.fontSize
      : activeObject?.fontSize) ||
    20;

  await changeFontSize(currentSize - 2);
};


const toggleBold = async () => {
  const textObjects = getSelectedTextObjects();
  if (!textObjects.length) return;

  const makeBold = !textObjects.every(
    (obj) => obj.fontWeight === "bold"
  );

  textObjects.forEach((obj) => {
    obj.set({ fontWeight: makeBold ? "bold" : "normal" });
  });

  await refreshTextObjects(textObjects);
};

const toggleItalic = async () => {
  const textObjects = getSelectedTextObjects();
  if (!textObjects.length) return;

  const makeItalic = !textObjects.every(
    (obj) => obj.fontStyle === "italic"
  );

  textObjects.forEach((obj) => {
    obj.set({ fontStyle: makeItalic ? "italic" : "normal" });
  });

  await refreshTextObjects(textObjects);
};

const toggleUnderline = async () => {
  const textObjects = getSelectedTextObjects();
  if (!textObjects.length) return;

  const makeUnderline = !textObjects.every(
    (obj) => Boolean(obj.underline)
  );

  textObjects.forEach((obj) => {
    obj.set({ underline: makeUnderline });
  });

  await refreshTextObjects(textObjects);
};

const changeAlignment = async (align) => {
  const textObjects = getSelectedTextObjects();
  if (!textObjects.length) return;

  textObjects.forEach((obj) => {
    obj.set({ textAlign: align });
  });

  await refreshTextObjects(textObjects);
};

  // SAVE DESIGN
  const saveDesign = () => {

    const designData =
      canvasRef.current.toJSON();

    console.log(designData);

    alert("Design Saved");
  };

  const fonts = [
  "Arial",
  "Georgia",
  "Courier New",
  "Roboto",
  "Poppins",
  "Montserrat",
  "Playfair Display",
];


useEffect(() => {
  const canvas = new fabric.Canvas("editor-canvas", {
    width: PAGE_SIZES.portrait.width,
    height: PAGE_SIZES.portrait.height,
    backgroundColor: "#fff",
    preserveObjectStacking: true,
  });


  canvasRef.current = canvas;
  canvasReadyRef.current = true;

canvas.on(
    "mouse:dblclick",
    (opt) => {
        const target =
            opt.target;

        if (!target) return;

        // Double-click a regular photo to enter crop mode.
if (
  target.type === "image" &&
  target.objectType !== "student-photo"
) {
  startCrop(target);
  return;
}

        /*
        =====================================================
        DIRECT TEXT OBJECT
        =====================================================
        */

        if (
            target.objectType ===
            "student-text"
        ) {
            /*
            -------------------------------------------------
            Store the text being edited.
            -------------------------------------------------
            */

            editingStudentTextRef.current =
                target;

            /*
            -------------------------------------------------
            If it has a parent group, keep GROUP active.
            -------------------------------------------------
            */

            const group =
                canvas
                    .getObjects()
                    .find(
                        (item) =>
                            item.objectType ===
                                "student-group" &&
                            item._objects?.includes(
                                target
                            )
                    );

            if (group) {
                canvas.setActiveObject(
                    group
                );

                selectedObjectRef.current =
                    group;

                setSelectedObject(
                    group
                );

                setToolbarVisible(
                    true
                );

                setToolbarState({
                    fontWeight:
                        target.fontWeight ||
                        "normal",

                    fontStyle:
                        target.fontStyle ||
                        "normal",

                    textAlign:
                        target.textAlign ||
                        "left",

                    underline:
                        !!target.underline,
                });
            }

            /*
            -------------------------------------------------
            Enter text editing.
            -------------------------------------------------
            */

            target.enterEditing();
            target.selectAll();

            canvas.requestRenderAll();

            return;
        }

        /*
        =====================================================
        STUDENT GROUP
        =====================================================
        */

        if (
            target.objectType ===
            "student-group"
        ) {
            /*
            -------------------------------------------------
            Find the nested text that was double-clicked.
            -------------------------------------------------
            */

const pointer = opt.pointer;

const subTarget =
    opt.subTargets?.find(
        (obj) =>
            obj.objectType === "student-text"
    ) ||
    target.getObjects().find((obj) => {
        if (obj.objectType !== "student-text") {
            return false;
        }

        if (!pointer) {
            return false;
        }

        const point = new fabric.Point(
            pointer.x,
            pointer.y
        );

        return obj.containsPoint(point);
    });

if (!subTarget) {
    console.log(
        "No student text detected. Double-click directly on the name, nickname, or motto."
    );
    return;
}

            /*
            -------------------------------------------------
            KEEP THE GROUP AS ACTIVE OBJECT
            -------------------------------------------------
            */

            editingStudentTextRef.current =
                subTarget;

            canvas.setActiveObject(
                target
            );

            selectedObjectRef.current =
                target;

            setSelectedObject(
                target
            );

            setToolbarVisible(
                true
            );

            setToolbarState({
                fontWeight:
                    subTarget.fontWeight ||
                    "normal",

                fontStyle:
                    subTarget.fontStyle ||
                    "normal",

                textAlign:
                    subTarget.textAlign ||
                    "left",

                underline:
                    !!subTarget.underline,
            });

            /*
            -------------------------------------------------
            ENTER TEXT EDITING WITHOUT MAKING THE
            TEXTBOX THE ACTIVE CANVAS OBJECT.
            -------------------------------------------------
            */

            subTarget.enterEditing();
            subTarget.selectAll();

            canvas.requestRenderAll();
        }
    }
);

   enableSmartGuides(canvas);

  const updateSelection = () => {
    const obj = canvas.getActiveObject();

    if (obj) {
      selectedObjectRef.current = obj;
      setSelectedObject(obj);
      setToolbarVisible(true);
      setToolbarState({
        fontWeight: obj.fontWeight || "normal",
        fontStyle: obj.fontStyle || "normal",
        textAlign: obj.textAlign || "left",
      });
      return;
    }

    if (selectedObjectRef.current) {
      setSelectedObject(selectedObjectRef.current);
      setToolbarVisible(true);
      setToolbarState({
        fontWeight: selectedObjectRef.current.fontWeight || "normal",
        fontStyle: selectedObjectRef.current.fontStyle || "normal",
        textAlign: selectedObjectRef.current.textAlign || "left",
      });
      return;
    }

    selectedObjectRef.current = null;
    setSelectedObject(null);
    setToolbarVisible(false);
    setToolbarState({
      fontWeight: "normal",
      fontStyle: "normal",
      textAlign: "left",
      underline: false,
    });
  };

const autoSave = async () => {
  if (isLoadingRef.current || isBulkUploadRef.current) return;

  // Keep the rest of your existing autoSave code below.

  const pageId = activePageRef.current;

  try {
    const json = canvas.toJSON();

    const preview = await createThumbnail(json);

    // IMPORTANT:
    // Ignore this result if the user has already
    // switched to another page while thumbnail was generating.
    if (activePageRef.current !== pageId) {
      return;
    }

    const updated = pagesRef.current.map((page) =>
      page.id === pageId
        ? {
            ...page,
            json,
            preview:
              preview ||
              page.preview ||
              page.thumbnail ||
              "",
          }
        : page
    );

    pagesRef.current = updated;
    setPages([...updated]);

  } catch (error) {
    console.error(
      "❌ Auto-save preview failed:",
      error
    );
  }
};



canvas.on("selection:created", updateSelection);
canvas.on("selection:updated", updateSelection);

canvas.on(
    "selection:cleared",
    () => {
        /*
        -------------------------------------------------
        If a student text is currently being edited,
        don't destroy the group selection state.
        -------------------------------------------------
        */

        if (
            editingStudentTextRef.current
        ) {
            const group =
                canvas
                    .getObjects()
                    .find(
                        (item) =>
                            item.objectType ===
                                "student-group" &&
                            item._objects?.includes(
                                editingStudentTextRef.current
                            )
                    );

            if (group) {
                canvas.setActiveObject(
                    group
                );

                selectedObjectRef.current =
                    group;

                setSelectedObject(
                    group
                );

                setToolbarVisible(
                    true
                );

                return;
            }
        }

        if (
            ignoreSelectionClearRef.current
        ) {
            ignoreSelectionClearRef.current =
                false;

            syncSelectedObject();

            return;
        }

        selectedObjectRef.current =
            null;

        setSelectedObject(
            null
        );

        setToolbarVisible(
            false
        );

        editingStudentTextRef.current =
            null;
    }
);

canvas.on(
    "mouse:down",
    (event) => {
        if (!event.target) {
            editingStudentTextRef.current =
                null;

            selectedObjectRef.current =
                null;

            setSelectedObject(
                null
            );

            setToolbarVisible(
                false
            );
        }
    }
);

  canvas.on("object:added", autoSave);
  canvas.on("object:modified", autoSave);
  canvas.on("object:removed", autoSave);


  return () => {
    canvas.dispose();
  };
}, []);




const preparePageJsonForCanvas = (pageJson) => {
  let json = pageJson;

  if (typeof json === "string") {
    json = JSON.parse(json);
  }

  if (!json || typeof json !== "object") {
    return json;
  }

  // Set CORS for remote Fabric images, including images
  // nested inside groups.
  const prepareObject = (obj) => {
    if (!obj || typeof obj !== "object") return;

    if (
      obj.type === "image" &&
      typeof obj.src === "string" &&
      /^https?:\/\//i.test(obj.src)
    ) {
      obj.crossOrigin = "anonymous";
    }

    if (Array.isArray(obj.objects)) {
      obj.objects.forEach(prepareObject);
    }

    if (Array.isArray(obj._objects)) {
      obj._objects.forEach(prepareObject);
    }
  };

  (json.objects || []).forEach(prepareObject);

  if (json.backgroundImage) {
    prepareObject(json.backgroundImage);
  }

  return json;
};

const createThumbnail = async (
  pageJson,
  orientation = "portrait"
) => {
  const size =
    PAGE_SIZES[orientation] || PAGE_SIZES.portrait;
  let tempCanvas = null;

  try {
    const preparedJson = preparePageJsonForCanvas(pageJson);

    const tempElement = document.createElement("canvas");

    tempCanvas = new fabric.StaticCanvas(tempElement, {
width: size.width,
height: size.height,
      backgroundColor: "#ffffff",
      enableRetinaScaling: false,
    });

    await tempCanvas.loadFromJSON(preparedJson);

    tempCanvas.setDimensions({
      width: size.width,
      height: size.height,
    });

    tempCanvas.setZoom(1);
    tempCanvas.renderAll();

    // A smaller thumbnail reduces memory and request size.
    
return tempCanvas.toDataURL({
  format: "png",
  multiplier: 1,
});

  } catch (error) {
    console.error("Thumbnail generation failed:", error);
    return null;
  } finally {
    if (tempCanvas) {
      await tempCanvas.dispose();
    }
  }
};


useEffect(() => {
  const updateScale = () => {
    const workspace = document.querySelector(".workspace");
    if (!workspace || !canvasRef.current) return;

const size =
  PAGE_SIZES[pageOrientation] || PAGE_SIZES.portrait;

const pageWidth = size.width;
const pageHeight = size.height;

    const availableWidth = workspace.clientWidth - 40;
    const availableHeight = workspace.clientHeight - 40;

    const scale = Math.min(
      availableWidth / pageWidth,
      availableHeight / pageHeight
    );

    setScale(scale);

    // Keep Fabric at real size
    canvasRef.current.setDimensions({
      width: pageWidth,
      height: pageHeight,
    });

    canvasRef.current.setViewportTransform([1, 0, 0, 1, 0, 0]);

    canvasRef.current.requestRenderAll();
  };

  updateScale();

  window.addEventListener("resize", updateScale);

  return () => window.removeEventListener("resize", updateScale);
}, []);

  return (
    <div className="design-studio">

{/* TOP HEADER */}
<div className="design-top">

    <div className="header-left">
        <h3>Yearbook Design Studio</h3>
        <span>{pages.length} Pages</span>
    </div>



    <div className="toolbar-center">

      

        <div
          className={`header-toolbar ${toolbarVisible ? "visible" : "hidden"}`}
          onMouseDown={handleToolbarPointerDown}
        >

{toolbarVisible && showTextToolbar && (
  <>
<select
    value={
        editingStudentTextRef.current
            ?.fontFamily ||
        selectedObject?.fontFamily ||
        fonts[0]
    }
    onMouseDown={(e) => e.stopPropagation()}
    onChange={(e) => changeFontFamily(e.target.value)}
>
        {fonts.map((font) => (
          <option
            key={font}
            value={font}
          >
            {font}
          </option>
        ))}
      </select>

<div className="font-size-box">

<button
    onMouseDown={(e) => e.preventDefault()}
    onClick={decreaseFont}
>
    <FiMinus />
</button>

<input
    type="number"
    min="6"
    onMouseDown={(e) =>
        e.preventDefault()
    }
    value={
        editingStudentTextRef.current?.fontSize ||
        selectedObject?.fontSize ||
        28
    }
    onChange={(e) =>
        changeFontSize(
            Number(e.target.value)
        )
    }
/>

<button
    onMouseDown={(e) => e.preventDefault()}
    onClick={increaseFont}
>
    <FiPlusCircle />
</button>

</div>

<button
    
    onMouseDown={(e) => e.preventDefault()}
    onClick={toggleBold}
    className={
        toolbarState.fontWeight === "bold"
            ? "active-tool"
            : ""
    }
>
    <FiBold/>
</button>

<button
    onMouseDown={(e) => e.preventDefault()}
    
    onClick={toggleItalic}
    className={
        toolbarState.fontStyle === "italic"
            ? "active-tool"
            : ""
    }
>
    <FiItalic/>
</button>

<button
    onMouseDown={(e) => e.preventDefault()}
    onClick={toggleUnderline}
    className={
        toolbarState.underline
            ? "active-tool"
            : ""
    }
>
    <FiUnderline/>
</button>

<button
    
    onMouseDown={(e) => e.preventDefault()}
    onClick={()=>changeAlignment("left")}
>
    <FiAlignLeft />
</button>

<button
    
    onMouseDown={(e) => e.preventDefault()}
    onClick={()=>changeAlignment("center")}
    className={
        toolbarState.textAlign === "center"
        ? "active-tool"
        : ""
    }
>
    <FiAlignCenter/>
</button>

<button
    onMouseDown={(e) => e.preventDefault()}
    onClick={()=>changeAlignment("right")}
    className={
        toolbarState.textAlign === "right"
        ? "active-tool"
        : ""
    }
>
    <FiAlignRight/>
</button>

<input
    type="color"
   
    onMouseDown={(e)=>e.preventDefault()}
    onChange={(e)=>changeColor(e.target.value)}
/>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={duplicateObject}
>
  <FiCopy />
</button>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={bringForward}
>
  <FiArrowUp />
</button>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={sendBackward}
>
  <FiArrowDown />
</button>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={bringToFront}
>
  <FiChevronsUp />
</button>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={sendToBack}
>
  <FiChevronsDown />
</button>

<button
  
    onMouseDown={(e) => e.preventDefault()}
    onClick={deleteObject}
>
    Delete
</button>

  </>
  )}


  {/* SHAPE TOOLS */}
{toolbarVisible && showShapeToolbar && (
      <>
        <input
          type="color"
          value={selectedObject.fill || "#6366f1"}
          onChange={(e) =>
            changeColor(e.target.value)
          }
        />

        <button
  onMouseDown={(e) => e.preventDefault()}
  onClick={duplicateObject}
>
  <FiCopy />
</button>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={bringForward}
>
  <FiArrowUp />
</button>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={sendBackward}
>
  <FiArrowDown />
</button>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={bringToFront}
>
  <FiChevronsUp />
</button>

<button
  onMouseDown={(e) => e.preventDefault()}
  onClick={sendToBack}
>
  <FiChevronsDown />
</button>

        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={deleteObject}
        >
          Delete
        </button>
      </>
  )}

</div>

</div>

<div className="save-menu">

<button
  type="button"
  className="your-existing-toolbar-button"
  onClick={() => setShowPdfImport(true)}
  disabled={isPublishing || isSavingDraft}
>
  Import PDF
</button>

  <button
    onClick={() => setShowPublishMenu(!showPublishMenu)}
  >
    Publish
  </button>

  {showPublishMenu && (
    <div className="save-dropdown">

      <button
        onClick={() => {
          setShowPublishModal(!showPublishModal);
        }}
      >
        Publish Yearbook
      </button>


<button
  onClick={() => {
    setShowPublishMenu(false);
    handleSaveDraft();
  }}
  disabled={isSavingDraft || isPublishing}
>
  {isSavingDraft ? "Saving Draft..." : "Save Draft"}
</button>

{/* Publish Modal */}
{showPublishModal && (
  <div className="publish-modal-overlay">

    <div className="publish-form">

      <h2>Publish Yearbook</h2>

      <label>Title</label>
      <input
        value={publishData.title}
        onChange={(e) =>
          setPublishData({
            ...publishData,
            title: e.target.value,
          })
        }
      />

      <label>Batch Name</label>
      <input
        value={publishData.batch_name}
        onChange={(e) =>
          setPublishData({
            ...publishData,
            batch_name: e.target.value,
          })
        }
      />

      <label>Academic Year</label>
      <input
        value={publishData.academic_year}
        onChange={(e) =>
          setPublishData({
            ...publishData,
            academic_year: e.target.value,
          })
        }
      />

      <label>Description</label>
      <textarea
        rows={4}
        value={publishData.description}
        onChange={(e) =>
          setPublishData({
            ...publishData,
            description: e.target.value,
          })
        }
      />


{imageUploadStatus.total > 0 && (
  <div
    className="image-upload-status"
    role="status"
    aria-live="polite"
    style={{ margin: "16px 0" }}
  >
    {imageUploadStatus.uploading ? (
      <>
        <p>
          Uploading images: {imageUploadStatus.completed} of{" "}
          {imageUploadStatus.total} processed
        </p>

        <progress
          value={imageUploadStatus.completed}
          max={imageUploadStatus.total}
          style={{ width: "100%" }}
        />

        <small>Please wait before publishing.</small>
      </>
    ) : imageUploadStatus.failed > 0 ? (
      <p>
        {imageUploadStatus.failed} image(s) failed.
        Remove and re-upload them before publishing.
      </p>
    ) : (
      <p>✓ All {imageUploadStatus.total} images uploaded successfully.</p>
    )}
  </div>
)}


      <div className="publish-actions">
        <button onClick={() => setShowPublishModal(false)}>
          Cancel
        </button>

      
<button
  onClick={publishYearbook}
  disabled={
    isPublishing ||
    imageUploadStatus.uploading ||
    imageUploadStatus.failed > 0
  }
>
  {isPublishing
    ? "Publishing..."
    : imageUploadStatus.uploading
      ? "Uploading Images..."
      : imageUploadStatus.failed > 0
        ? "Resolve Failed Uploads"
        : "Publish"}
</button>

      </div>

    </div>

  </div>
)}

    </div>
  )}

</div>

</div>

      {/* BODY */}
      <div className="studio-body">

        {/* LEFT SIDEBAR */}
        <aside className="left-sidebar">

        <div className="left-sidebar-content">

          <h5>ADD</h5>

          <button
            className="tool-btn"
            onClick={addText}
          >
            <FiType />
            Add Text
          </button>

<button
  className="tool-btn"
  onClick={() =>
    setShowShapesPanel(
      !showShapesPanel
    )
  }
>
  <FiSquare />
  Add Shape
</button>

{showShapesPanel && (
  <div className="shapes-card">

    <h4>Shapes</h4>

    <div className="shapes-grid">

      <div
        className="shape-item"
        onClick={addShape}
      >
        <div className="shape-preview rectangle" />
      </div>

      <div
        className="shape-item"
        onClick={addCircle}
      >
        <div className="shape-preview circle" />
      </div>

      <div
        className="shape-item"
        onClick={addTriangle}
      >
        <div className="shape-preview triangle" />
      </div>

    </div>

  </div>
)}

 <button
    className="tool-btn"
    onClick={() =>
        setShowTemplatePanel(!showTemplatePanel)
    }
>
<FiGrid />
Templates
</button>

{showTemplatePanel && (
  <div className="template-panel">

    <div
      className="template-card"
      onClick={async () => {
        await handleApplyTemplate();
        setShowTemplatePanel(false);
      }}
    >
      <div className="template-preview">
        ▱
      </div>

      <div className="template-name">
        Simple Template
      </div>
    </div>

  </div>
)}

<button
  className="tool-btn"
  onClick={() => {
    syncInsertedStudentIds();
    setShowStudentsPanel(true);
  }}
>
  <FiUsers size={18} />
  <span>Students</span>
</button>

<button
  className="tool-btn"
  onClick={() => setShowFacultyPanel(true)}
>
  <FiBookOpen size={18} />
  <span>Faculty</span>
</button>

<button
  className="tool-btn"
  onClick={() => setShowMemoriesPanel(true)}
>
  <FiMessageCircle size={18} />
  <span>Memories/Message</span>
</button>


          <label className="tool-btn">
    <FiImage />
    Upload Images

    <input
        hidden
        type="file"
        multiple
        accept="image/*"
        onChange={handleUploadImages}
    />
</label>

{/* STUDENT LAYOUT PICKER */}
<div className="student-layout-picker">
  <h4>Student Layout</h4>

  <p className="student-layout-description">
    Choose a layout for your student profiles.
  </p>

  <div className="student-layout-options">
    {STUDENT_LAYOUT_OPTIONS.map((layout) => (
      <button
        key={layout.id}
        type="button"
        className={`student-layout-option ${
          studentLayout === layout.id ? "active" : ""
        }`}
        onClick={() => setStudentLayout(layout.id)}
      >
        <div
          className="student-layout-preview"
          style={{
            "--layout-columns": layout.columns,
          }}
        >
          {Array.from(
            { length: layout.capacity },
            (_, index) => (
              <span key={index} />
            )
          )}
        </div>

        <span className="student-layout-name">
          {layout.label}
        </span>

        <small>
          {layout.capacity} students
        </small>

        {studentLayout === layout.id && (
          <span className="student-layout-check">
            ✓
          </span>
        )}
      </button>
    ))}
  </div>
</div>

</div>

</aside>

        {/* CENTER WORKSPACE */}
        <main className="workspace">

          <div className="canvas-container">


<div
  className="canvas-wrapper"
  style={{
    "--editor-scale": editorScale,
    width: PAGE_SIZES[pageOrientation].width,
    height: PAGE_SIZES[pageOrientation].height,
    flex: `0 0 ${PAGE_SIZES[pageOrientation].width}px`,
  }}
>
  <div
    className="yearbook-page"
    style={{
      width: PAGE_SIZES[pageOrientation].width,
      height: PAGE_SIZES[pageOrientation].height,
      flex: `0 0 ${PAGE_SIZES[pageOrientation].width}px`,
    }}
  >
    <canvas id="editor-canvas"></canvas>
  </div>
</div>


          
<span className="page-size">
  A4 {pageOrientation} · {
    PAGE_SIZES[pageOrientation].width
  } × {
    PAGE_SIZES[pageOrientation].height
  }
</span>


            

          </div>

        </main>

        {/* RIGHT SIDEBAR */}
        <aside className="right-sidebar">


          <div className="pages-scroll">

{pages.map((page) => (
  <div
    key={page.id}
    draggable
    className={`page-drag-wrapper ${
      dragOverPageId === page.id ? "page-drag-over" : ""
    }`}
    
    onDragStart={(e) => {
      setDraggedPageId(page.id);

      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData(
        "text/plain",
        String(page.id)
      );
    }}

    onDragOver={(e) => {
      e.preventDefault();

      e.dataTransfer.dropEffect = "move";

      if (dragOverPageId !== page.id) {
        setDragOverPageId(page.id);
      }
    }}

    onDragLeave={() => {
      setDragOverPageId(null);
    }}

    onDrop={(e) => {
      e.preventDefault();

      const draggedId = Number(
        e.dataTransfer.getData("text/plain")
      );

      const targetId = page.id;

      if (!draggedId || draggedId === targetId) {
        setDraggedPageId(null);
        setDragOverPageId(null);
        return;
      }

      const currentPages = [...pagesRef.current];

      const draggedIndex = currentPages.findIndex(
        (p) => p.id === draggedId
      );

      const targetIndex = currentPages.findIndex(
        (p) => p.id === targetId
      );

      if (
        draggedIndex === -1 ||
        targetIndex === -1
      ) {
        return;
      }

      // Remove dragged page
      const [draggedPage] =
        currentPages.splice(draggedIndex, 1);

      // Find target again after removal
      const newTargetIndex =
        currentPages.findIndex(
          (p) => p.id === targetId
        );

      // Insert before target
      currentPages.splice(
        newTargetIndex,
        0,
        draggedPage
      );

      // Update page titles
      const reorderedPages =
        currentPages.map((p, index) => ({
          ...p,
          title:
            p.type === "faculty"
              ? p.title
              : index === 0
              ? "Cover"
              : `Page ${index + 1}`,
        }));

      pagesRef.current = reorderedPages;
      setPages(reorderedPages);

      setDraggedPageId(null);
      setDragOverPageId(null);
    }}

    onDragEnd={() => {
      setDraggedPageId(null);
      setDragOverPageId(null);
    }}
  >
<PagePreview
  page={page}
  active={activePage === page.id}
  onClick={() => switchPage(page.id)}
  onDelete={deletePage}
/>
  </div>
))}
          </div>

          {
pages.find(
 p => p.id === activePage
)?.type === "faculty"
?
<button
 className="add-page-button"
 onClick={addFacultyPage}
>
 <FiPlus />
 Add Faculty Page
</button>

:

<button
 className="add-page-button"
 onClick={addPage}
>
 <FiPlus />
 Add Page
</button>

}

        </aside>

      </div>


{/* ==========================================
    FACULTY MODAL
========================================== */}

{showFacultyPanel && (
  <FacultyList
    faculty={facultyWithMessages}
    onInsert={insertSelectedFaculty}
    onClose={() => setShowFacultyPanel(false)}
    insertedFacultyIds={
      [
        ...new Set(
          (canvasRef.current?.getObjects() || [])
            .map((obj) => obj.facultyId)
            .filter(Boolean)
        )
      ]
    }
  />
)}


{showMemoriesPanel && (
  <MemoryList
    memories={memories}
    onInsert={insertSelectedMemories}
    onClose={() =>
      setShowMemoriesPanel(false)
    }
    insertedMemoryIds={
      getInsertedMemoryIds()
    }
  />
)}


  
{showStudentsPanel && (
  <StudentList
    students={students}
    onInsert={insertStudents}
    onClose={() => setShowStudentsPanel(false)}
    insertedStudentIds={insertedStudentIds}
  />
)}

{isPublishing && (
  <div className="publish-loading-overlay">
    <div
      className="publish-loading-card"
      role="status"
      aria-live="polite"
    >
      <div className="publish-spinner" />

      <h2>Publishing Yearbook</h2>

      <p>{publishStatus}</p>

      <div className="publish-progress-track">
        <div
          className="publish-progress-fill"
          style={{ width: `${publishProgress}%` }}
        />
      </div>

      <span>{publishProgress}% uploaded</span>

      <small>
        Please wait. Do not close or refresh this page.
      </small>
    </div>
  </div>
)}


{isSavingDraft && (
  <div
    className="publish-loading-overlay"
    role="status"
    aria-live="polite"
  >
    <div className="publish-loading-card">
      <div className="publish-spinner" />

      <h2>Saving Yearbook Draft</h2>

      <p>{draftStatus}</p>

      <div className="publish-progress-track">
        <div
          className="publish-progress-fill"
          style={{ width: "100%" }}
        />
      </div>

      <small>
        Please wait while your yearbook pages and cover
        preview are saved. Do not refresh or close this page.
      </small>
    </div>
  </div>
)}


{showPdfImport && (
  <PdfYearbookImport
    onClose={() => setShowPdfImport(false)}
    onImportPages={handlePdfPagesImported}
  />
)}


{/* REUSABLE DESIGN STUDIO MESSAGE MODAL */}
{studioModal.open && (
  <div
    className="studio-modal-overlay"
    onClick={closeStudioModal}
  >
    <div
      className="studio-message-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="studio-modal-title"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="studio-message-close"
        aria-label="Close modal"
        onClick={closeStudioModal}
      >
        ×
      </button>

      <div
        className={`studio-message-icon studio-message-icon--${studioModal.type}`}
      >
        {studioModal.type === "success"
          ? "✓"
          : studioModal.type === "error"
          ? "!"
          : studioModal.type === "warning"
          ? "!"
          : "i"}
      </div>

      <h2 id="studio-modal-title">
        {studioModal.title}
      </h2>

      <p>{studioModal.message}</p>

      <div className="studio-message-actions">
        <button
          type="button"
          className="studio-message-primary"
          onClick={handleStudioModalConfirm}
        >
          {studioModal.buttonText}
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );


  
}

export default DesignStudio;
